import { useFormik } from "formik";
import {
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  Box,
  Breadcrumbs,
  Divider,
  Card,
  IconButton,
  Autocomplete,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Collapse,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  OutlinedInput,
  ListItemText,
  FormHelperText,
  Chip,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import PillToggle from "../../components/PillToggle";
import PaymentAttachmentUpload from "../../components/PaymentAttachmentUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { Fragment, useRef, useState, useMemo } from "react";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Editor styles

import { apiData } from "../../services/api_service";
import {
  fetchProductDropdownAPI,
  fetchProductUnitAPI,
  insertOrderAPI,
  submitFocRequestAPI,
  focRequestBaseAPI,
  fetchOrdernumberAPI,
  fetchOrderByIdAPI,
  updateOrderByIdAPI,
  termsAndConditionsAPI,
  storesDropdownAPI,
  fetchProductsByStoreAPI,
  fetchCustomerDDAPI,
  productAvailabilityAPI,
  fetchBundlesAPI,
  invoiceAccessAPI,
  uploadToVercelAPI,
  bundleAvailabilityAllStoresAPI,
  insertCreditApprovalAPI,
} from "../../config";
import { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import dayjs from "dayjs";
import { emirates } from "../../components/constants";
import { formatAED } from "../../utils/format";
import { discountAmountFromPercent, sanitizePercentInput } from "../../utils/discount";
import { LoadingButton } from "@mui/lab";
import { Backdrop, CircularProgress } from "@mui/material";

const isKnownEmirate = (value) =>
  emirates.some((emirate) => emirate.name === value);

// UAE-specific format requirement removed (Item 8) — Contact Number is
// optional; only a loose generic sanity check applies when a value is typed.
const validateUaePhone = (value) => {
  if (!value) return true;
  const clean = value.replace(/[\s-]/g, "");
  const genericPhoneRegex = /^\+?\d{6,15}$/;
  if (!genericPhoneRegex.test(clean)) {
    return "Enter a valid phone number";
  }
  return true;
};

const parseFocPayload = (payload) => {
  if (!payload) return {};
  if (typeof payload === "object") return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return {};
  }
};

const getWholeFocReason = (payload, items = []) => {
  const directReason =
    payload?.wholeFocReason ||
    payload?.bulkFocReason ||
    payload?.whole_foc_reason ||
    payload?.focReason ||
    "";

  if (String(directReason).trim()) return directReason;

  const focReasons = (Array.isArray(payload?.items) ? payload.items : items)
    .filter((item) => item?.isFoc || item?.is_foc)
    .map((item) => item?.focReason || item?.foc_reason || "")
    .filter((reason) => String(reason).trim());

  const uniqueReasons = [...new Set(focReasons.map((reason) => String(reason).trim()))];
  return uniqueReasons.length === 1 ? uniqueReasons[0] : "";
};

// Upper bound for any single money field (price, discount, shipping). Generous
// for a real transaction, but stops stray/mashed keystrokes from producing
// absurd (even exponential-notation) totals.
const MAX_MONEY_VALUE = 9999999.99;

// Sanitizes free-typed money input: strips anything that isn't a digit or a
// decimal point, caps decimals at 2 places, and clamps to `max`. Always
// returns a usable value instead of silently rejecting the keystroke — a
// reject-and-return-early approach leaves the real input text out of sync
// with the value React thinks it set, letting more digits pile up uncapped
// on the next keystroke.
const sanitizeMoneyInput = (raw, max) => {
  let sanitized = String(raw).replace(/[^\d.]/g, "");
  const firstDot = sanitized.indexOf(".");
  if (firstDot !== -1) {
    sanitized =
      sanitized.slice(0, firstDot + 1) +
      sanitized.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
  }
  const numeric = parseFloat(sanitized);
  if (sanitized !== "" && !isNaN(numeric) && numeric > max) {
    return max.toFixed(2);
  }
  return sanitized;
};

// Sanity cap on Credit Days — unbounded input overflows dayjs().add() into
// "Invalid Date" wherever a Due Date is derived from it.
const MAX_CREDIT_DAYS = 3650; // 10 years

const Orders = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const alertRef = useRef(null);
  const { id, focRequestId } = useParams();

  const navigate = useNavigate();
  const isEdit = Boolean(id);
  // Reached from FOC Approvals ("View") — same form, fully read-only. Loads
  // either the actual invoice (approved request, real order_id) or the
  // as-submitted draft payload (pending/rejected — never became an order).
  const isViewOnly = Boolean(focRequestId);
  const [focRequestMeta, setFocRequestMeta] = useState(null);

  const [productList, setProductList] = useState([]);
  // True while fetchProductsWithStock is in flight *for a store change*
  // specifically (not for a keystroke search — see the two call sites below)
  // — used to keep the product picker disabled until the fresh store's stock
  // has actually loaded, instead of letting openOnFocus pop it open showing
  // whatever the previous store's (or the initial no-store) list was.
  const [productListLoading, setProductListLoading] = useState(false);
  const [bundleList, setBundleList] = useState([]);
  const [selectionMode, setSelectionMode] = useState("product"); // "product" | "bundle"
  // On-demand cross-store lookup for an out-of-stock bundle: bundleId ->
  // { loading, byStore }. Fetched lazily, only when staff click "Check other
  // stores" on that specific bundle, so it stays cheap no matter how many
  // stores or bundles exist.
  const [bundleStoreAvailability, setBundleStoreAvailability] = useState({});
  const checkBundleOtherStores = async (bundleProductId) => {
    setBundleStoreAvailability((prev) => ({
      ...prev,
      [bundleProductId]: { loading: true, byStore: [] },
    }));
    try {
      const res = await apiData(
        "get",
        `${bundleAvailabilityAllStoresAPI}/${bundleProductId}`
      );
      setBundleStoreAvailability((prev) => ({
        ...prev,
        [bundleProductId]: {
          loading: false,
          byStore: res.success ? res.data?.byStore || [] : [],
        },
      }));
    } catch (e) {
      console.error("Failed to fetch bundle availability across stores:", e);
      setBundleStoreAvailability((prev) => ({
        ...prev,
        [bundleProductId]: { loading: false, byStore: [] },
      }));
    }
  };
  const [customerList, setCustomerList] = useState([]);
  const [searchTerm, setSearchTerm] = useState([""]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // FOC (Item 8) bulk toggle — "whole invoice is complimentary" convenience:
  // checks every current row's FOC box with one shared reason instead of
  // ticking each line individually. Purely a UI shortcut; same per-line data.
  const [bulkFocOpen, setBulkFocOpen] = useState(false);
  const [bulkFocReason, setBulkFocReason] = useState("");
  // Per-row collapsible state for the (optional) FOC reason field — keyed by
  // item index, collapsed by default so a row with FOC checked stays compact
  // until the reason is actually needed.
  const [expandedFocReasons, setExpandedFocReasons] = useState({});
  const [initialValues, setInitialValues] = useState(null);
  const [originalPaymentMethod, setOriginalPaymentMethod] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [changeDateDialogOpen, setChangeDateDialogOpen] = useState(false);
  const [newOrderDate, setNewOrderDate] = useState(null);
  const [changeReason, setChangeReason] = useState("");
  const [savingChangeDate, setSavingChangeDate] = useState(false);
  const [originalOrderDate, setOriginalOrderDate] = useState(null);
  const [bundleDetails, setBundleDetails] = useState(null);

  // Credit terms (payment_type === 1) confirmation — the PDF prints as a
  // "CREDIT INVOICE" instead of "TAX INVOICE", so the user gets one last
  // explicit heads-up before it's generated.
  const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
  // Edit mode: was this invoice already approved for credit when opened?
  const [wasCredit, setWasCredit] = useState(false);
  // Why the user is asking for credit — shown to Admin alongside the
  // invoice, kept separate from the invoice's own Notes field.
  const [creditReason, setCreditReason] = useState("");
  // Edit-mode Payment Type change (Credit <-> Immediate) is functionally the
  // same "settle/convert" decision as the Payment Status column on the list
  // page, so it needs the same explicit confirm before saving — the backend
  // now logs it to tbl_invoice_conversion_history either way.
  const [paymentTypeChangeConfirmOpen, setPaymentTypeChangeConfirmOpen] = useState(false);

  // Guard direct URL access (e.g. typing /invoice/add or /invoice/edit/:id
  // manually) against the current user's create/edit invoice permission —
  // the sidebar link is hidden, but the route itself must also be blocked.
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiData("get", invoiceAccessAPI);
        const access = res.success && res.data ? res.data : {};
        const allowed = isEdit
          ? access.can_edit_invoice !== false
          : access.can_create_invoice !== false;
        if (!allowed) {
          setAccessDenied(true);
          setTimeout(() => navigate(-1), 5000);
        }
      } catch (e) {
        console.error("Failed to fetch invoice access:", e);
      } finally {
        setAccessChecked(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    orderDate: Yup.string().required("Order Date is required"),
    name: Yup.string()
      .required("Customer Name is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),

    // Mirrors the Customer Master form ([[Customers.jsx]]) — a new customer
    // typed directly on this form (i.e. no existing customer picked from the
    // "Select Customer" dropdown) is auto-created in tbl_customers on submit,
    // so it needs the same Individual/Company classification the master form
    // collects, instead of silently defaulting to "Company" on the backend.
    customer_type: Yup.string().required("Customer type is required"),
    // Only required when creating a new customer here. An existing customer
    // selected from the dropdown may predate the Company Name field on
    // Customer Master and simply have none on file — don't block the order
    // on filling in master-data that this form isn't responsible for.
    company_name: Yup.string().when(["customer_type", "customer_id"], {
      is: (customer_type, customer_id) => customer_type === "Company" && !customer_id,
      then: (schema) =>
        schema
          .required("Company Name is required")
          .max(255, "Maximum 255 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),

    phone_number: Yup.string()
      .nullable()
      .test("valid-phone", (value, ctx) => {
        const result = validateUaePhone(value);
        return result === true ? true : ctx.createError({ message: result });
      }),

    // po_box: Yup.string().required("P.O Box is required"),
    // Email is optional (Item 8) — basic format check only when provided.
    email: Yup.string()
      .nullable()
      .test("valid-email", "Enter a valid email address", (value) => {
        if (!value) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }),

    address: Yup.string()
      // .required("Street Address is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _,-]+$/,
        "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
      ),

    emirate: Yup.string()
      // .required("Emirate is required")
      .nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          productId: Yup.string().nullable().required("Product is required"),
          // Add this inside your validationSchema to debug
          sellingPrice: Yup.number()
            .required("Selling price is required")
            .moreThan(0, "Must be greater than 0")
            .max(MAX_MONEY_VALUE, "Selling price is too large")
            .test("is-greater", function (value) {
              const { buyingPrice } = this.parent;

              if (!buyingPrice) return true;
              if (Number(value) < Number(buyingPrice)) {
                return this.createError({
                  message: `Selling price (${value}) cannot be lesser than buying price (${buyingPrice})`,
                });
              }
              return true;
            }),
          quantity: Yup.number()
            .transform((value, originalValue) =>
              String(originalValue).trim() === "" ? null : value
            )
            .nullable()
            .required("Quantity is required")
            .typeError("Quantity must be number")
            .moreThan(0, "Must be greater than 0"),
          subtotal: Yup.number()
            .required("Subtotal is required")
            .min(0, "Cannot be negative"),
          buyingPrice: Yup.number().nullable(),
        })
      )
      .min(1, "At least 1 product must be selected"),

    vat_no: Yup.string()
      // .required("VAT No. is required")
      // `*` (not `+`) so a blank, optional field doesn't fail validation —
      // Yup's .matches() checks empty strings by default, and `+` requires
      // at least one digit, which used to reject "" with a confusing
      // "Only numbers are allowed" error on an untouched field.
      .matches(/^[0-9]*$/, "Only numbers are allowed"),

    // trn_number: Yup.string().required("TRN is required"),

    taxAmount: Yup.number()
      .required("Tax amount is required")
      .min(0, "Cannot be negative"),

    discount: Yup.number()
      .min(0, "Cannot be negative")
      .max(MAX_MONEY_VALUE, "Discount is too large")
      .test(
        "not-more-than-subtotal",
        "Discount cannot exceed the subtotal",
        function (value) {
          const items = this.parent.items || [];
          const subtotal = items.reduce(
            (sum, row) => sum + (parseFloat(row.subtotal) || 0),
            0
          );
          return (parseFloat(value) || 0) <= subtotal;
        }
      ),

    shippingCharges: Yup.number()
      .min(0, "Cannot be negative")
      .max(MAX_MONEY_VALUE, "Shipping charges is too large"),

    term_id: Yup.string().required("Terms & Conditions is required"),
    paymentMethod: Yup.string().when("payment_type", {
      is: 2,
      then: (schema) => schema.required("Payment Method is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    payment_type: Yup.number().required("Payment Type is required"),
    store_id: Yup.number().required("Store is required"),
  });

  const formik = useFormik({
    initialValues: {
      orderDate: dayjs().format("YYYY-MM-DD"),
      order_date_change_reason: "",      
      customer_id: null,
      store_id: "",
      orderNumber: "",
      name: "",
      customer_type: "Company",
      company_name: "",
      phone_number: "",
      email: "",
      address: "",
      vat_no: "",
      po_box: "",
      emirate: "",
      emirateOption: "",
      items: [{ productId: "", sellingPrice: 0, quantity: 0, subtotal: 0 }],
      taxType: "5",
      taxMode: "exclusive", // "exclusive" (VAT added on top) | "inclusive" (VAT embedded in price)
      taxAmount: 0,
      shippingCharges: 0,
      discount: 0,
      // "item" = Item-wise Discount (per product row), "total" = Total Special
      // Discount (single value on the whole transaction). Products only —
      // bundles keep their own separate discount mode. Mutually exclusive
      // per Item 3's requirement.
      discountType: "item",
      bundleDiscount: 0,
      payment_type: "",
      paymentMethod: "",
      paymentReferenceNo: "",
      paymentAttachmentUrl: "",
      paymentAttachmentName: "",
      paymentAttachmentUploading: false,
      wholeFocReason: "",
      creditDays: "",
      dueDate: dayjs().format("YYYY-MM-DD"),
      creditDescription: "",
      transactionId: "",
      notes: "",
      term_id: "",
      terms: "",
      orderStatus: "Completed",
      trn_number: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      let hasOutOfStock = false;

      // Only validate stock if this is a new order
      if (!id) {
        hasOutOfStock = values.items.some((item) => {
          // Bundle rows are never stocked as a product themselves — their
          // availability is derived from their components (maxQuantity, the min
          // buildable across components). Validate against that, not against the
          // parent "Bundle" product's own (always false) inStock flag, which
          // would otherwise block every bundle order.
          if (item.isBundle) {
            const max = Number(item.maxQuantity) || 0;
            return max < 1 || Number(item.quantity) > max;
          }
          const product = productList.find((p) => p.id === item.productId);
          return product && !product.inStock;
        });
      }

      if (hasOutOfStock) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: "You cannot place an order with out-of-stock products.",
            messageType: "error",
          })
        );
        return; // stop submission
      }
      try {
        setLoading(true);
        values.user_id = user.emp_id;
        // Only used by the FOC request path (approvals list display) — see
        // submitFocRequestAPI below; harmless no-op for a normal invoice.
        // Login (sp_Authentication) returns the logged-in employee's name as
        // `username`, not `employee_name`/`name` — those two are kept as a
        // fallback only in case a different login path ever populates them.
        values.requested_by_name = user?.username || user?.employee_name || user?.name || "";

        // Bundle discounts (bundle-wide or per-item) stay fully hidden on the
        // printed invoice: fold into a NET unit price (subtotal ÷ qty) so it
        // reads as price × qty = line total with no discount shown. A plain
        // product's discount is real customer-facing information, so it's
        // kept visible instead — sellingPrice stays the actual (gross) price
        // and the discount amount rides along separately for the PDF to
        // print as its own column.
        const payload = {
          ...values,
          items: values.items.map((item) => {
            if (item.fromBundle) {
              const qty = parseFloat(item.quantity) || 0;
              const net = parseFloat(item.subtotal) || 0;
              const netUnit = qty > 0 ? net / qty : 0;
              return { ...item, sellingPrice: Number(netUnit.toFixed(2)) };
            }
            // discountPercent is a 0-100 % now — backend's discount_amount
            // column still stores an actual currency value. Prefer the AED
            // value the user actually typed (discountValue) over
            // recomputing it from the rounded % — e.g. AED 10 on a 150 line
            // rounds to a displayed 6.67%, and 150 × 6.67% comes back as
            // 10.005 → 10.01, not the original 10.00. Only fall back to
            // computing from % when that's genuinely what was entered (or
            // discountValue is missing, e.g. an older record).
            const gross =
              (parseFloat(item.sellingPrice) || 0) * (parseFloat(item.quantity) || 0);
            const discountValueNum = parseFloat(item.discountValue);
            const discountAmt =
              item.discountUnit === "percent" || Number.isNaN(discountValueNum)
                ? discountAmountFromPercent(gross, item.discountPercent)
                : discountValueNum;
            return discountAmt > 0
              ? { ...item, discount_amount: Number(discountAmt.toFixed(2)) }
              : item;
          }),
        };

        // FOC (Item 8): any line marked Free of Charge routes this away from
        // the normal create/update endpoints entirely — it becomes a pending
        // request instead, and only turns into a real invoice once the Chief
        // Executive approves it (see models/focModel.js on the backend). The
        // FOC checkbox is disabled while editing, so this can't fire on id.
        const hasFoc = !id && payload.items.some((item) => item.isFoc);

        // Credit is approved per document: a NEW credit invoice isn't created
        // here — its full form goes to Admin as a draft, and the server only
        // creates it once Admin approves. The confirm dialog already told the
        // user this is happening (see creditConfirmOpen below), so this just
        // does it — no second popup.
        if (!id && !hasFoc && String(payload.payment_type) === "1") {
          if (!payload.customer_id) {
            alertRef.current.triggerAlert(
              JSON.stringify({
                message: "Select an existing customer from the list to request credit terms.",
                messageType: "warning",
              })
            );
            return;
          }
          const creditResult = await apiData("post", insertCreditApprovalAPI, {
            customer_id: payload.customer_id,
            requested_days: Number(payload.creditDays) || 15,
            notes: creditReason || "",
            doc_type: "invoice",
            payload,
          });
          if (creditResult.success) {
            alertRef.current.triggerAlert(
              JSON.stringify({
                message: "Sent to Admin for credit approval. This invoice will be created once approved.",
                messageType: "success",
              })
            );
            setTimeout(() => navigate("/credit-approval/list"), 1400);
          } else {
            alertRef.current.triggerAlert(
              JSON.stringify({
                message: creditResult.msg || "Failed to send the credit approval request.",
                messageType: "error",
              })
            );
          }
          return;
        }

        let result;
        if (hasFoc) {
          result = await apiData("post", submitFocRequestAPI, payload);
        } else if (id) {
          result = await apiData("put", `${updateOrderByIdAPI}/${id}`, payload);
        } else {
          result = await apiData("post", insertOrderAPI, payload);
        }

        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );
          setTimeout(() => {
            navigate(hasFoc ? "/foc/approvals" : "/order/list");
          }, 7000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: error.msg,
            messageType: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchOrder();
  }, [id]);

  useEffect(() => {
    if (focRequestId) fetchFocRequestForView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focRequestId]);

  // The saved VAT rate for an order. Newer orders store it in tax_type; older
  // rows only have the amounts, so infer the rate from those: a taxable base
  // greater than zero with no tax charged means the invoice was saved at 0%.
  const deriveTaxType = (order) => {
    if (order?.tax_type !== undefined && order?.tax_type !== null && order?.tax_type !== "") {
      return String(order.tax_type);
    }

    const subtotal = parseFloat(order?.subtotal || 0);
    const discount = parseFloat(order?.discount_amount || 0);
    const taxableBase = subtotal - discount;
    const taxAmount = parseFloat(order?.tax_amount || 0);

    if (taxableBase > 0 && taxAmount === 0) return "0";
    return "5";
  };

  // moved fetchOrder to outer scope so it can be reused by handlers.
  // overrideId lets the FOC "View" path (which has no :id route param, only
  // :focRequestId) load an already-approved request's real invoice by its
  // order_id instead.
  const fetchOrder = async (overrideId) => {
    const targetId = overrideId || id;
    if (targetId) {
      try {
        const result = await apiData("get", `${fetchOrderByIdAPI}/${targetId}`);

      if (result.success && result.data) {
        const order = result.data.order;
        const items = result.data.items || [];
        const payment = result.data.payments?.[0] || {};

        const formattedDate = order.order_date
          ? dayjs(order.order_date).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD");

        setOriginalOrderDate(formattedDate);

        const values = {
          customer_id: order.customer_id ? parseInt(order.customer_id) : null,
          orderDate: formattedDate,
          // tbl_orders itself doesn't store customer_name/phone/email/address/
          // emirate/po_box/vat_no — those live on tbl_customers and come back
          // from getOrderDetails as the customer_* aliases (order.name etc.
          // are only kept as a fallback in case a legacy column exists).
          // "Bill To Company" is order.company_name, joined the same way.
          name: order.customer_name || order.name || "",
          customer_type: order.customer_type || "Company",
          company_name: order.company_name || "",
          billToCompany: order.company_name || order.customer_name || order.name || "",
          store_id: order.store_id || "",
          phone_number: order.customer_phone || order.phone_number || "",
          email: order.customer_email || order.email || "",
          address: order.customer_address || order.address || "",
          emirate: order.customer_emirate || order.emirate || "",
          emirateOption: (() => {
            const val = order.customer_emirate || order.emirate || "";
            if (!val) return "";
            return isKnownEmirate(val) ? val : "Others";
          })(),
          po_box: order.customer_po_box || order.po_box || "",
          vat_no: order.customer_vat_no || order.vat_no || "",
          items: items.map((item) => ({
            productId: item.product_id || "",
            productName: item.product_name || "",
            sellingPrice: parseFloat(item.selling_price || 0),
            quantity: item.quantity || 0,
            subtotal: parseFloat(item.total_price || 0),
            originalQuantity: item.quantity || 0,
            maxQuantity: item.quantity || 0,
            // Round-trip the per-item discount so it shows in the Discount
            // field on reopen instead of looking like it was never applied.
            // discount_amount is a currency (AED) value — it belongs in
            // discountValue, NOT discountPercent (that was a bug: it copied
            // the raw AED number straight into the % box, e.g. a real
            // AED 10 discount on a 150 line reading as "10%" instead of the
            // correct ~6.67%). Derive the % properly from amount ÷ gross.
            discountUnit:
              parseFloat(item.discount_amount || 0) > 0 ? "amount" : undefined,
            discountValue:
              parseFloat(item.discount_amount || 0) > 0
                ? String(parseFloat(item.discount_amount).toFixed(2))
                : undefined,
            discountPercent: (() => {
              const discAmt = parseFloat(item.discount_amount || 0);
              if (discAmt <= 0) return undefined;
              const gross =
                parseFloat(item.selling_price || 0) * (parseFloat(item.quantity) || 0);
              return gross > 0 ? ((discAmt / gross) * 100).toFixed(2) : undefined;
            })(),
            // Read-only on reopen — FOC checkbox is disabled while editing
            // (see the row UI), so this only ever displays what an approved
            // FOC request already set, never lets it be toggled here.
            isFoc: !!item.is_foc,
            focReason: item.foc_reason || "",
            focValue: parseFloat(item.foc_value || 0).toFixed(2),
          })),
          // Show the rate the invoice was saved with, not the 5% default.
          // Orders created before tax_type was stored have no saved rate, so
          // fall back to deriving it from the amounts (0 tax on a non-zero
          // taxable base can only mean 0%).
          taxType: deriveTaxType(order),
          // Both of these drive which recalculation formula runs on any
          // further edit (qty/discount/shipping change) — losing them meant
          // the edit screen silently reset to "exclusive"/"item", and
          // editing an Inclusive-tax or Total-discount order would recompute
          // the whole summary under the wrong mode, drifting the Grand Total
          // away from what the customer was actually invoiced.
          taxMode: order.tax_mode || "exclusive",
          discountType: order.discount_type || "item",
          subtotalSummary: parseFloat(order.subtotal || 0),
          taxAmount: parseFloat(order.tax_amount || 0),
          shippingCharges: parseFloat(order.shipping_amount || 0),
          discount: parseFloat(order.discount_amount || 0),
          grandTotal: parseFloat(order.grand_total || 0),
          payment_type:
            payment.payment_type !== undefined ? payment.payment_type : 0,
          paymentMethod: order.payment_method,
          paymentReferenceNo: payment.transaction_ref || "",
          paymentAttachmentUrl: payment.payment_attachment_url || "",
          paymentAttachmentName: payment.payment_attachment_name || "",
          creditDays: payment.credit_days,
          dueDate: payment.due_date,
          creditDescription: payment.credit_description,
          transactionId: payment.transaction_id,
          notes: order.notes || "",
          terms: order.terms || "",
          term_id: order.term_id || "",
          trn_number: order.trn_number || "",
        };
        setInitialValues(values);
        formik.setValues(values);
        setOriginalPaymentMethod(order.payment_method);
        setWasCredit(String(payment.payment_type) === "1");
      } else if (!isViewOnly) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: result.msg,
            messageType: "error",
          })
        );
      }
    } catch (e) {
      console.error("Error fetching order:", e);
    }
  }
};

  // Reached only via /view-foc/:focRequestId (the "View" action on FOC
  // Approvals). An approved request has a real order_id — show that actual,
  // final invoice via the normal fetchOrder path. A pending/rejected one
  // never became an order, so there's nothing in tbl_orders to fetch yet —
  // load the as-submitted draft straight from the stored request payload
  // instead (same shape formik already expects, since it IS what got
  // submitted). Either way the form renders fully read-only (isViewOnly).
  const fetchFocRequestForView = async () => {
    try {
      setLoading(true);
      const res = await apiData("get", `${focRequestBaseAPI}/${focRequestId}`);
      if (!res.success || !res.data) {
        alertRef.current?.triggerAlert(
          JSON.stringify({ message: res.msg || "FOC request not found", messageType: "error" })
        );
        return;
      }
      const request = res.data;
      setFocRequestMeta(request);

      if (request.status === "approved" && request.order_id) {
        await fetchOrder(request.order_id);
        return;
      }

      const payload = parseFocPayload(request.payload);
      setOriginalOrderDate(
        payload.orderDate ? dayjs(payload.orderDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
      );
      const values = { ...formik.values, ...payload };
      setInitialValues(values);
      formik.setValues(values);
    } catch (error) {
      console.error("Failed to load FOC request for view", error);
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: "Failed to load this FOC request.", messageType: "error" })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWithValidation = async () => {
    await formik.validateForm();

    const errors = formik.errors;
    if (Object.keys(errors).length > 0) {
      const errorMessages = [];

      const fieldLabels = {
        orderDate: "Order Date",
        name: "Customer Name",
        phone_number: "Mobile Number",
        email: "Email",
        address: "Street Address",
        emirate: "Emirate",
        items: "Products",
        taxAmount: "Tax Amount",
        term_id: "Terms & Conditions",
        payment_type: "Payment Type",
        store_id: "Store",
      };

      Object.keys(errors).forEach((field) => {
        // "items" errors are per-row (one product/bundle line left
        // incomplete — e.g. an extra "Add Bundle"/"Add Product" row that
        // was never filled in) — a blanket "• Products" doesn't say which
        // row or why, so call out the specific row and reason instead.
        if (field === "items") {
          if (typeof errors.items === "string") {
            errorMessages.push(`• Products: ${errors.items}`);
          } else if (Array.isArray(errors.items)) {
            errors.items.forEach((rowErr, idx) => {
              if (!rowErr || typeof rowErr !== "object") return;
              Object.values(rowErr).forEach((msg) => {
                if (msg) errorMessages.push(`• Row ${idx + 1}: ${msg}`);
              });
            });
          }
          return;
        }
        const label = fieldLabels[field] || field.replace(/_/g, " ");
        errorMessages.push(`• ${label}`);
      });

      alertRef.current.triggerAlert(
        JSON.stringify({
          message: `Please fill in the following required fields:\n${errorMessages.join(
            "\n"
          )}`,
          messageType: "warning",
        })
      );
      return;
    }

    // Editing an existing invoice and actually changing Payment Type
    // (Credit <-> Immediate) is the same consequential decision as the
    // Settle Payment & Convert dialog on the list page — it flips paid
    // status, regenerates the PDF, and re-emails the customer — so it gets
    // its own explicit confirmation instead of silently riding along with
    // an unrelated field edit (e.g. a quantity change).
    const isPaymentTypeChangeOnEdit =
      Boolean(id) &&
      initialValues &&
      initialValues.payment_type !== "" &&
      Number(formik.values.payment_type) !== Number(initialValues.payment_type);

    if (isPaymentTypeChangeOnEdit) {
      setPaymentTypeChangeConfirmOpen(true);
      return;
    }

    // Credit terms chosen (payment_type === 1) — confirm once before actually
    // submitting. On edit this is just a recap (payment type unchanged, still
    // Credit); on create it's the "this goes to Admin for approval" notice —
    // see the dialog content below, which branches on `id`.
    if (String(formik.values.payment_type) === "1") {
      setCreditConfirmOpen(true);
      return;
    }

    formik.handleSubmit();
  };

  const addRow = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", sellingPrice: "", quantity: 0, subtotal: "" },
    ]);
    setSearchTerm([...searchTerm, ""]);
  };

  const removeRow = (idx) => {
    const nextItems = [...formik.values.items];
    const nextSearch = [...searchTerm];
    nextItems.splice(idx, 1);
    nextSearch.splice(idx, 1);
    formik.setFieldValue("items", nextItems);
    setSearchTerm(nextSearch);
    recalcSummary(nextItems);
  };

  const recalcSummary = (
    items,
    shippingOverride,
    discountOverride,
    taxTypeOverride,
    bundleDiscountOverride,
    taxModeOverride
  ) => {
    // Bundle rows carry a per-item discount (%) and share the overall
    // bundle discount (%). Both reduce each line's net amount but are
    // NEVER itemised on the printed proforma/invoice — the customer only
    // ever sees the reduced line totals plus the usual Special Discount and
    // VAT. Every discount is a 0-100 percentage (never a currency amount),
    // so a line/bundle can go to 0 but never negative.
    const pricedItems = items.map((row) => {
        const gross =
          (parseFloat(row.sellingPrice) || 0) * (parseFloat(row.quantity) || 0);
        let net = gross;
        if (row.fromBundle) {
          // Per bundle, EITHER one bundle-wide % OR per-item % applies —
          // never both. row.discountMode selects which ("bundle" default
          // | "item").
          if ((row.discountMode || "bundle") === "item") {
            const itemDisc = discountAmountFromPercent(gross, row.discountPercent);
            net = gross - itemDisc;
          } else {
            // Bundle-wide % is applied to the whole bundle's actual price,
            // then spread across its lines in proportion to each line's own
            // share of the bundle — so per-row Subtotal boxes still sum
            // exactly to the discounted bundle total.
            const groupGross = items
              .filter((it) => it.bundleGroupId === row.bundleGroupId)
              .reduce(
                (s, it) =>
                  s + (parseFloat(it.sellingPrice) || 0) * (parseFloat(it.quantity) || 0),
                0
              );
            const bundleDiscAmt = discountAmountFromPercent(groupGross, row.bundleDiscount);
            const share = groupGross > 0 ? gross / groupGross : 0;
            net = gross - bundleDiscAmt * share;
          }
        } else {
          // Plain (non-bundle) product row: supports both % and currency amount
          let itemDisc = 0;
          if (row.discountUnit === "amount" || row.discountUnit === "value") {
            itemDisc = parseFloat(row.discountValue || 0);
            if (itemDisc > gross) itemDisc = gross;
          } else {
            itemDisc = discountAmountFromPercent(gross, row.discountPercent);
          }
          net = gross - itemDisc;
        }
        const netFloor = Math.max(net, 0);
        // FOC (Item 8): the customer is charged nothing for this line — its
        // subtotal goes to 0 — but VAT is still due on what the line would
        // normally have cost. focValue keeps that "would-have-been" amount
        // so the tax base below can add it back in, and so it survives to
        // the invoice/report as the value of the freebie.
        if (row.isFoc) {
          return { ...row, subtotal: "0.00", focValue: netFloor.toFixed(2) };
        }
        return { ...row, subtotal: netFloor.toFixed(2), focValue: "0.00" };
      });

    const subtotal = pricedItems.reduce(
      (sum, row) => sum + (parseFloat(row.subtotal) || 0),
      0
    );
    // focValue is what a FOC line's price/discount would have come to. The
    // item is free so it stays out of the Subtotal, but its VAT IS still
    // charged below (deemed supply) — focValue feeds the tax base, not 0.

    const discount =
      parseFloat(discountOverride ?? formik.values.discount) || 0;

    const taxRate = parseFloat(taxTypeOverride ?? formik.values.taxType) || 0;
    const taxMode = taxModeOverride ?? formik.values.taxMode ?? "exclusive";

    const shipping =
      parseFloat(shippingOverride ?? formik.values.shippingCharges) || 0;

    // What the unit price itself MEANS changes between the two modes — this
    // is the actual difference between them, not just which field you type
    // into:
    //  - Exclusive: the price on each product row is the pre-tax base. Tax
    //    is calculated fresh and added on top at the end. (Unchanged —
    //    this is exactly how it worked before.)
    //  - Inclusive: the price on each product row is treated as ALREADY
    //    containing VAT — e.g. a 45 AED item and a 50 AED item sum to a 95
    //    AED Subtotal that already has tax folded into it. Tax here isn't
    //    added, it's EXTRACTED back out of that 95 to show the breakdown
    //    (Amount before VAT ≈ 90.48, Tax ≈ 4.52), and the Grand Total stays
    //    the sum of what was actually typed on the rows (95 + shipping) —
    //    it does not go up by 5% on top of that.
    // Deemed supply (UAE VAT): a FOC line is free to the customer, but VAT is
    // still due on what it would have cost. focGross sums those would-have-been
    // values; its VAT is added to the tax below and the customer pays it, so a
    // fully-FOC order's Grand Total equals the VAT on the freebies.
    const focGross = pricedItems.reduce(
      (sum, row) => sum + (row.isFoc ? parseFloat(row.focValue) || 0 : 0),
      0
    );
    let netSubtotal;
    let taxAmount;
    let grandTotal;
    if (taxMode === "inclusive") {
      const discountedSubtotal = Math.max(subtotal - discount, 0);
      // Back the pre-tax base out of the tax-inclusive figure (÷ (1 + rate/100)).
      netSubtotal = discountedSubtotal / (1 + taxRate / 100);
      // The FOC value is tax-inclusive here too, so its VAT is the portion
      // already sitting inside it.
      const focTax = focGross - focGross / (1 + taxRate / 100);
      taxAmount = discountedSubtotal - netSubtotal + focTax;
      grandTotal = discountedSubtotal + focTax + shipping;
    } else {
      const discountedSubtotal = subtotal - discount;
      netSubtotal = discountedSubtotal > 0 ? discountedSubtotal : 0;
      // VAT is charged on the FOC value even though the item itself is free.
      const focTax = (focGross * taxRate) / 100;
      taxAmount = (netSubtotal * taxRate) / 100 + focTax;
      grandTotal = netSubtotal + taxAmount + shipping;
    }
    // Round to cents — these values feed a directly-editable numeric field
    // (Grand Total, in Inclusive mode) as well as submission payloads, and
    // floating-point division above (e.g. taxAmount = x * rate / (100 +
    // rate)) otherwise leaks values like 411.07500000000005 straight into
    // the UI.
    netSubtotal = parseFloat(netSubtotal.toFixed(2));
    taxAmount = parseFloat(taxAmount.toFixed(2));
    grandTotal = parseFloat(grandTotal.toFixed(2));

    // Functional form — merges onto whatever state is actually pending at
    // apply time, instead of spreading the stale `formik.values` snapshot
    // captured when this closure was created. Callers that do
    // formik.setFieldValue(...) right before calling recalcSummary (e.g.
    // resetting "discount" to 0 on a mode switch) would otherwise have that
    // update silently reverted by this call overwriting it with the old
    // pre-click value.
    formik.setValues((prev) => ({
      ...prev,
      items: pricedItems,
      subtotalSummary: netSubtotal, // ✅ This excludes shipping
      taxAmount,
      taxMode,
      shippingCharges: shipping, // ✅ keep shipping separate
      grandTotal,
    }));
  };

  const handleProductSelect = async (idx, product) => {
    if (!product || !formik.values.store_id) return;

    try {
      const result = await apiData(
        "get",
        `${fetchProductUnitAPI}/${product.id}/${formik.values.store_id}`
      );

      if (result.success) {
        const fetchedSellingPrice = result.data?.[0]?.selling_price
          ? parseFloat(result.data[0].selling_price)
          : 0;

        const fetchedBuyingPrice = result.data?.[0]?.buying_price
          ? parseFloat(result.data[0].buying_price)
          : 0;

        const updatedItems = [...formik.values.items];

        // If user has not typed any price → use fetched selling price
        // Always use the newly selected product's fetched price, so switching a
        // row's product refreshes the price (it previously kept the old price).
        const finalSellingPrice = fetchedSellingPrice;

        updatedItems[idx] = {
          ...updatedItems[idx],
          productId: product.id,
          buyingPrice: fetchedBuyingPrice,
          sellingPrice: finalSellingPrice,
          quantity: updatedItems[idx].quantity || 1,
          subtotal: (
            finalSellingPrice * (updatedItems[idx].quantity || 1)
          ).toFixed(2),
          maxQuantity: product.atp ?? product.quantity,
        };

        formik.setFieldValue("items", updatedItems);
        recalcSummary(updatedItems);
      }
    } catch (error) {
      console.error("Failed to fetch product unit price:", error);
    }
  };

  const handleQuantityChange = (idx, value) => {
    const updatedItems = [...formik.values.items];

    if (value === "") {
      updatedItems[idx].quantity = "";
      updatedItems[idx].subtotal = 0;
      recalcSummary(updatedItems);
      return; // ⛔ skip rest
    }

    // Bundle rows cap at the bundle's derived max (min of component stock).
    if (updatedItems[idx].isBundle) {
      const bundleMax = Number(updatedItems[idx].maxQuantity) || 0;
      let finalValue = parseInt(value, 10) || 0;
      if (bundleMax > 0 && finalValue > bundleMax) {
        finalValue = bundleMax;
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: `Only ${bundleMax} bundle(s) can be made from current stock`,
            messageType: "error",
          })
        );
      }
      updatedItems[idx].quantity = finalValue;
      updatedItems[idx].subtotal =
        (parseFloat(updatedItems[idx].sellingPrice) || 0) * finalValue;
      recalcSummary(updatedItems);
      return;
    }

    const initialItems = initialValues ? [...initialValues.items] : [];

    const product = productList.find(
      (p) => p.id === updatedItems[idx].productId
    );

    // if (product && !product.inStock) {
    //   alertRef.current.triggerAlert(
    //     JSON.stringify({
    //       message: "This product is Out of Stock",
    //       messageType: "error",
    //     })
    //   );
    //   updatedItems[idx].quantity = 0;
    //   updatedItems[idx].subtotal = 0;
    // } else {
    // Cap at Available-to-Promise (physical − reserved), falling back to physical.
    const stockQty = (product?.atp ?? product?.quantity) || 0;
    const originalQty = updatedItems[idx]?.originalQuantity || 0;
    // const derivedMax = id ? stockQty + (initialItems[idx].maxQuantity || 0) : stockQty;
    let atpMax;
    if (id && initialItems[idx]) {
      atpMax = stockQty + (initialItems[idx].maxQuantity || 0);
    } else {
      atpMax = stockQty;
    }

    // ATP is per-product, not per-row — if the same product is already
    // picked in another row of this document, that quantity has to come out
    // of the pool too, or two rows can each claim the full ATP and together
    // request more stock than actually exists.
    const qtyUsedElsewhere = updatedItems.reduce((sum, row, i) => {
      if (i === idx || row.isBundle || row.productId !== updatedItems[idx].productId) {
        return sum;
      }
      return sum + (parseFloat(row.quantity) || 0);
    }, 0);
    const derivedMax = Math.max(atpMax - qtyUsedElsewhere, 0);

    let finalValue = parseInt(value, 10);
    if (atpMax > 0 && finalValue > derivedMax) {
      // user tried to set more than what's left after other rows' shares
      finalValue = derivedMax;
      const elsewhereNote =
        qtyUsedElsewhere > 0 ? ` (${qtyUsedElsewhere} already used in another row)` : "";
      alertRef.current.triggerAlert(
        JSON.stringify({
          message: `Maximum allowed quantity is ${derivedMax}${elsewhereNote}`,
          messageType: "error",
        })
      );
    }

    updatedItems[idx].quantity = finalValue;
    updatedItems[idx].subtotal =
      (parseFloat(updatedItems[idx].sellingPrice) || 0) * finalValue;

    recalcSummary(updatedItems);
  };

  const fetchProducts = async (search = "") => {
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );

      if (result.success) {
        setProductList(result.data.result);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  const fetchStores = async () => {
    try {
      const result = await apiData("get", storesDropdownAPI);
      if (result.success) {
        setStoreOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  const fetchProductsWithStock = async (storeId = null, search = "") => {
    try {
      // Always get all products first
      const allProductsResult = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );
      let allProducts = [];
      if (allProductsResult.success) {
        allProducts = allProductsResult.data.result.map((p) => ({
          id: p.id,
          product_name: p.product_name,
          sku: p.sku,
          product_image: p.product_image,
          quantity: 0, // default: out of stock
          inStock: false,
        }));
      }

      if (storeId) {
        // Get stock for this store
        const storeResult = await apiData(
          "get",
          `${fetchProductsByStoreAPI}?id=${storeId}&search=${search}`
        );
        if (storeResult.success) {
          const stockMap = {};
          storeResult.data.forEach((p) => {
            stockMap[p.product_id] = Number(p.quantity) || 0;
          });

          // Merge stock info into all products
          allProducts = allProducts.map((p) => ({
            ...p,
            quantity: stockMap[p.id] || 0,
            inStock: !!stockMap[p.id],
          }));
        }

        // Overlay Available-to-Promise: subtract stock reserved by proformas so
        // the order picker shows/limits what can actually be committed.
        try {
          const availResult = await apiData("post", productAvailabilityAPI, {
            store_id: storeId,
            product_ids: allProducts.map((p) => p.id),
          });
          if (availResult.success && Array.isArray(availResult.data)) {
            const atpMap = {};
            availResult.data.forEach((a) => {
              atpMap[a.product_id] = a;
            });
            allProducts = allProducts.map((p) => {
              const a = atpMap[p.id];
              if (!a) return { ...p, reserved: 0, atp: p.quantity };
              return {
                ...p,
                quantity: a.onHand,
                reserved: a.reserved,
                expired: a.expired || 0,
                atp: a.atp,
                inStock: a.atp > 0,
              };
            });
          }
        } catch (availErr) {
          console.error("Failed to fetch availability (ATP):", availErr);
        }
      }

      setProductList((prev) => {
        // Map previous state for preserving selected and reference matching
        const prevMap = new Map(prev.map((p) => [p.id, p]));

        return allProducts.map((p) => {
          const old = prevMap.get(p.id);
          return old ? { ...old, ...p } : p;
        });
      });
    } catch (error) {
      console.error("Failed to fetch products:", error);
      setProductList([]);
    }
  };

  // Bundles with per-store component availability (eligible / max_qty computed
  // server-side). A bundle is a UI grouping; its stock comes from components.
  const fetchBundles = async (storeId = null, search = "") => {
    try {
      const params = new URLSearchParams();
      if (storeId) params.append("store_id", storeId);
      if (search) params.append("search", search);
      const result = await apiData(
        "get",
        `${fetchBundlesAPI}?${params.toString()}`
      );
      if (result.success) {
        setBundleList(result.data.result || []);
      }
    } catch (error) {
      console.error("Failed to fetch bundles:", error);
      setBundleList([]);
    }
  };

  // One toggle for the whole item list. Product rows and bundle rows are shaped
  // differently, so switching resets to a single empty row.
  const handleModeChange = (mode) => {
    if (!mode || mode === selectionMode) return;
    setSelectionMode(mode);
    const emptyRow = { productId: "", sellingPrice: 0, quantity: 0, subtotal: 0 };
    formik.setFieldValue("items", [emptyRow]);
    setSearchTerm([""]);
    // Same reasoning as the Discount Method / Tax Mode / Tax Rate switches —
    // Products <-> Bundle is a fresh start too, so wipe Special Discount
    // instead of recalculating on top of stale values.
    recalcSummary([emptyRow], undefined, 0);
    formik.setFieldValue("discount", 0);
    formik.setFieldValue("discountPercent", "");
  };

  // A bundle carries no price of its own — its value is the sum of its
  // component products' CURRENT selling prices at the selected store.
  const getBundleStorePrice = (bundle) =>
    (bundle?.components || []).reduce(
      (sum, c) =>
        sum + Number(c.selling_price || 0) * Number(c.required_per_bundle || 1),
      0
    );

  const getBundleForRow = (row) =>
    bundleList.find((bundle) => bundle.id === row.productId) || null;

  const handleBundleSelect = (idx, bundle) => {
    if (!bundle) {
      const updatedItems = [...formik.values.items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        productId: null,
        isBundle: false,
        bundleId: null,
        bundleGroupId: null,
        sellingPrice: 0,
        quantity: 0,
        subtotal: 0,
        maxQuantity: 0,
        bundleDiscount: 0,
      };
      formik.setFieldValue("items", updatedItems);
      recalcSummary(updatedItems);
      return;
    }

    const bundleGroupId = `${bundle.id}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 6)}`;

    // A bundle is just a group of products: explode it into individual product
    // lines priced at each component's CURRENT selling price (looked up from the
    // store's product list). Any discount is applied afterwards on the invoice
    // per bundle group, never on the bundle itself. The form switches to normal
    // product mode so the exploded lines behave like any other rows.
    const lines = (bundle.components || []).map((c) => {
      const price = Number(c.selling_price || 0);
      const qty = Number(c.required_per_bundle || 1);
      return {
        productId: c.product_id,
        isBundle: false,
        // Came from a bundle — the product itself is locked (can't be swapped),
        // though its price/quantity remain editable.
        bundleId: bundle.id,
        bundleGroupId,
        bundleName: bundle.product_name,
        bundleSku: bundle.sku,
        fromBundle: true,
        // "bundle" = one % for the whole bundle | "item" = per-product %.
        discountMode: "bundle",
        sellingPrice: price,
        quantity: qty,
        subtotal: (price * qty).toFixed(2),
        maxQuantity: Number(c.available || 0),
        bundleDiscount: 0,
      };
    });

    // Explode the bundle into product lines in place of the picker row, and stay
    // on the current (Bundle) tab. The exploded rows render as product rows
    // because they now carry a productId (see the row picker condition).
    const updatedItems = [...formik.values.items];
    const updatedSearch = [...searchTerm];
    if (lines.length) {
      updatedItems.splice(idx, 1, ...lines);
      updatedSearch.splice(idx, 1, ...lines.map(() => ""));
    }

    formik.setFieldValue("items", updatedItems);
    setSearchTerm(updatedSearch);
    recalcSummary(updatedItems);
  };

  // Add this useEffect to handle store changes — the single place this
  // fires from (see the store Autocomplete's onChange below for why a
  // second, duplicate trigger there was removed). productListLoading keeps
  // the product picker disabled until this actually resolves, so
  // openOnFocus can't pop it open showing stale/previous-store stock.
  useEffect(() => {
    if (formik.values.store_id) {
      setProductListLoading(true);
      Promise.all([
        fetchProductsWithStock(formik.values.store_id),
        fetchBundles(formik.values.store_id),
      ]).finally(() => setProductListLoading(false));

      // Only reset items if they don't have productId selected
      const hasSelectedProducts = formik.values.items.some(
        (item) => item.productId
      );
      if (!hasSelectedProducts) {
        // Reset items to initial empty state but keep the structure
        const resetItems = formik.values.items.map((item) => ({
          ...item,
          productId: "",
          sellingPrice: 0,
          quantity: 0,
          subtotal: 0,
          buyingPrice: null,
        }));
        formik.setFieldValue("items", resetItems);
      }
    }
  }, [formik.values.store_id]);

  async function fetchCustomers() {
    const result = await apiData("get", fetchCustomerDDAPI);
    if (result.data?.result) {
      setCustomerList(result.data.result);
    }
  }

  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const fetchTermsAndConditions = async (existingTermId = null) => {
    try {
      const payload = { termsType: 2 };
      const result = await apiData("get", termsAndConditionsAPI, payload);
      if (result.success) {
        const termsList = result.data.result || [];
        setTermsAndConditions(termsList);

        if (existingTermId) {
          // If editing, use the term saved in the order
          const existingTerm = termsList.find(
            (term) => term.id === existingTermId
          );
          if (existingTerm) {
            formik.setFieldValue("term_id", existingTerm.id);
            formik.setFieldValue("terms", existingTerm.description || "");
          }
        } else {
          // Otherwise, use default
          const defaultTerm = termsList.find((term) => term.isDefault === 1);
          if (defaultTerm) {
            formik.setFieldValue("term_id", defaultTerm.id);
            formik.setFieldValue("terms", defaultTerm.description || "");
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch terms and conditions:", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchStores();
    fetchTermsAndConditions();
  }, []);

  const handleCustomerChange = (customer) => {
    const selectedCustId = customer ? customer.customer_id : "";
    formik.setFieldValue("customer_id", selectedCustId);
    formik.setFieldValue("name", customer ? customer.name : "");
    // Existing customer's own Individual/Company classification (set on the
    // Customer Master form) travels with the selection instead of being
    // silently reset — see the "Not in the list?" note in the JSX below.
    formik.setFieldValue(
      "customer_type",
      customer ? customer.customer_type || "Company" : "Company"
    );
    formik.setFieldValue(
      "company_name",
      customer ? customer.company_name || "" : ""
    );
    formik.setFieldValue(
      "billToCompany",
      customer ? customer.company_name || customer.name : ""
    );
    formik.setFieldValue("address", customer ? customer.address : "");
    const customerEmirate = customer ? customer.emirate : "";
    formik.setFieldValue("emirate", customerEmirate);
    formik.setFieldValue(
      "emirateOption",
      customerEmirate
        ? isKnownEmirate(customerEmirate)
          ? customerEmirate
          : "Others"
        : ""
    );
    formik.setFieldValue("email", customer ? customer.email : "");
    formik.setFieldValue("phone_number", customer ? customer.phone_number : "");
    formik.setFieldValue("po_box", customer ? customer.po_box : "");
    // `|| ""` (not just `? :`) — a customer with vat_no/trn_number stored as
    // null would otherwise fail Yup's string validation with a type error
    // instead of the intended "must be numeric" message.
    formik.setFieldValue("vat_no", customer?.vat_no || "");
    formik.setFieldValue("trn_number", customer?.trn_number || "");
  };

  let debounceTimer = null;
  const debounce = (fn, delay = 400) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, delay);
  };

  const paymentOptions = useMemo(() => {
    return [
      { value: "1", label: "Cash" },
      { value: "2", label: "Card" },
      { value: "3", label: "Cheque" },
      { value: "5", label: "Bank Transfer" },
      { value: "6", label: "Online Payment" },
      // Same as any other Immediate Payment method — no special handling
      // anywhere, just a label for how the customer is being billed.
      { value: "7", label: "Bill to Company" },
      // Only selectable once at least one line item is marked FOC (see
      // [[foc-given-report-feature]]) — otherwise there's nothing
      // complimentary about the order to justify this payment method.
      { value: "8", label: "Complimentary", requiresFoc: true },
    ];
  }, []);

  // Drives the Complimentary payment method's enabled state and its
  // disabled-state tooltip below — available as soon as any item is FOC.
  const hasFocItem = (formik.values.items || []).some((item) => item.isFoc);

  // Drives the Credit Payment Terms block below — a MIXED order (some FOC,
  // some paid) can still go on Credit for the paid items, so Credit is only
  // blocked once every real product row on the order is FOC (nothing left
  // to actually bill on credit).
  const focEligibleItems = (formik.values.items || []).filter(
    (item) => item.productId
  );
  const allItemsFoc =
    focEligibleItems.length > 0 &&
    focEligibleItems.every((item) => item.isFoc);

  // Complimentary has no actual payment to reference or attach proof of —
  // clear anything already entered so a stale reference no./attachment from
  // switching methods doesn't ride along under Complimentary.
  useEffect(() => {
    if (formik.values.paymentMethod === "8") {
      formik.setFieldValue("paymentReferenceNo", "");
      formik.setFieldValue("paymentAttachmentUrl", "");
      formik.setFieldValue("paymentAttachmentName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.paymentMethod]);

  useEffect(() => {
    if (formik.values.payment_type == 2) {
      formik.setFieldValue("creditDays", "");
      formik.setFieldValue("dueDate", null);
    }
  }, [formik.values.payment_type]);

  // An all-FOC order cannot be billed on Credit terms: if Credit was already
  // selected and every item then ends up marked FOC, force the order back to
  // Immediate Payment instead of leaving it sitting on a now-invalid
  // selection (the Select's disabled MenuItem above only stops a NEW
  // selection, it doesn't undo one already made). Credit for a non-FOC (or
  // mixed FOC/paid) order is still settled the normal way from the list page.
  useEffect(() => {
    if (allItemsFoc && Number(formik.values.payment_type) === 1) {
      formik.setFieldValue("payment_type", 2);
      formik.setFieldValue("creditDays", "");
      formik.setFieldValue("dueDate", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItemsFoc]);

  const handleOpenChangeDate = (order) => {
    setSelectedOrder(order);
    setNewOrderDate(order.order_date ? dayjs(order.order_date) : dayjs());
    setChangeReason("");
    setChangeDateDialogOpen(true);
  };

  const handleCloseChangeDate = () => {
    setChangeDateDialogOpen(false);
    setSelectedOrder(null);
    setNewOrderDate(null);
    setChangeReason("");
  };

  const handleOrderDateChange = (date) => {
    if (!date) return;

    const formatted = date.format("YYYY-MM-DD");

    // If editing & date changed → ask reason
    if (id && originalOrderDate && formatted !== originalOrderDate) {
      setNewOrderDate(date);
      setChangeDateDialogOpen(true);
      return; // ⛔ stop direct update
    }

    // Normal flow (new order OR same date)
    formik.setFieldValue("orderDate", formatted);

    if (Number(formik.values.paymentMethod) === 4 && formik.values.creditDays) {
      const newDueDate = dayjs(formatted)
        .add(Number(formik.values.creditDays), "day")
        .format("YYYY-MM-DD");

      formik.setFieldValue("dueDate", newDueDate);
    }
  };

  const handleSaveChangeDate = () => {
    if (!newOrderDate) {
      alert("Please select a date");
      return;
    }

    if (!changeReason || changeReason.trim().length < 3) {
      alert("Please enter reason");
      return;
    }

    const formatted = dayjs(newOrderDate).format("YYYY-MM-DD");

    // ✅ Update formik date
    formik.setFieldValue("orderDate", formatted);

    // ✅ Attach reason to payload
    formik.setFieldValue("order_date_change_reason", changeReason.trim());

    setOriginalOrderDate(formatted); // reset baseline
    handleCloseChangeDate();
  };


  if (accessDenied) {
    return (
      <Layout>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="60vh"
        >
          <AlertMessage ref={alertRef} />
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Sorry! You are unauthorized to {isEdit ? "edit" : "create"} invoices.
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Redirecting to the previous page...
          </Typography>
        </Box>
      </Layout>
    );
  }

  const wholeFocReason = (() => {
    if (!focRequestMeta?.payload) return "";
    const payload = parseFocPayload(focRequestMeta.payload);
    return getWholeFocReason(payload, formik.values.items);
  })();

  return (
    <Layout>
      {/* Change Order Date Dialog */}
      <Dialog
        open={changeDateDialogOpen}
        onClose={handleCloseChangeDate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change Order Date</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="New Order Date"
                value={newOrderDate}
                inputFormat="DD-MM-YYYY"
                onChange={(val) => setNewOrderDate(val)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
              />
            </LocalizationProvider>

            <TextField
              label="Reason for date change"
              fullWidth
              multiline
              rows={3}
              margin="normal"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Enter reason (required)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeDate}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={handleSaveChangeDate}
            loading={savingChangeDate}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Credit Invoice confirmation — payment_type === 1 (Credit) */}
      <Dialog
        open={creditConfirmOpen}
        onClose={() => setCreditConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {id ? "This is a Credit Invoice" : "Send for Credit Approval?"}
        </DialogTitle>
        <DialogContent>
          {id ? (
            <Typography variant="body2" color="text.secondary">
              Payment terms are set to <b>Credit</b>
              {formik.values.creditDays ? ` (${formik.values.creditDays} days)` : ""}.
              No payment is being collected now, and the printed document will
              say <b>"CREDIT INVOICE"</b> instead of "TAX INVOICE". Are you sure
              you want to proceed?
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                You selected <b>Credit Payment Terms</b>
                {formik.values.creditDays ? ` — ${formik.values.creditDays} days requested` : ""}.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This invoice <b>won't be created yet</b>. It's sent to Admin for
                approval first, and only becomes a real invoice once Admin approves it.
              </Typography>
              {creditReason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: "italic" }}>
                  Reason given: "{creditReason}"
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="warning"
            loading={loading}
            onClick={() => {
              setCreditConfirmOpen(false);
              formik.handleSubmit();
            }}
          >
            {id ? "Yes, Create Credit Invoice" : "Send for Admin Approval"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Edit-mode Payment Type change (Credit <-> Immediate) confirmation */}
      <Dialog
        open={paymentTypeChangeConfirmOpen}
        onClose={() => setPaymentTypeChangeConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Change Payment Type?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {Number(formik.values.payment_type) === 2 ? (
              <>
                Switching from <b>Credit</b> to <b>Immediate</b> will mark this
                invoice as <b>Paid</b>, regenerate it as a{" "}
                <b>TAX INVOICE</b>, and email the updated PDF to the customer.
              </>
            ) : (
              <>
                Switching from <b>Immediate</b> to <b>Credit</b> will revert this
                invoice's payment status to <b>Pending</b>, regenerate it as a{" "}
                <b>CREDIT INVOICE</b>, and email the updated PDF to the customer.
              </>
            )}{" "}
            This change is recorded in the invoice's conversion history. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPaymentTypeChangeConfirmOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="warning"
            loading={loading}
            onClick={() => {
              setPaymentTypeChangeConfirmOpen(false);
              if (String(formik.values.payment_type) === "1") {
                setCreditConfirmOpen(true);
              } else {
                formik.handleSubmit();
              }
            }}
          >
            Yes, Continue
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* <Box sx={{ p: { xs: 2, sm: 4, md: 6 } }}> */}
      <Grid className="employee-form">
        <AlertMessage ref={alertRef} />

        {/* Page Title + Breadcrumb */}
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Invoice
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Invoices</Typography>
              <Typography color="text.primary">Add</Typography>
            </Breadcrumbs>
          </Grid>

          {/* Actions */}
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="outlined"
              color="primary"
              className="button_first"
              onClick={() => navigate("/invoice/list")}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
            >
              Invoice Report
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="outlined"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <form onSubmit={formik.handleSubmit}>
          {isViewOnly && focRequestMeta && (
            <Box
              sx={{
                mt: 3,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                  focRequestMeta.status === "approved"
                    ? "#B7E4C7"
                    : focRequestMeta.status === "rejected"
                    ? "#F5B8B8"
                    : "#B8D7FF",
                backgroundColor:
                  focRequestMeta.status === "approved"
                    ? "#EAF7EE"
                    : focRequestMeta.status === "rejected"
                    ? "#FDECEC"
                    : "#EEF4FF",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                Viewing FOC Request {focRequestMeta.request_number} —{" "}
                {String(focRequestMeta.status).toUpperCase()} (read only)
              </Typography>
              {wholeFocReason && (
                <Box
                  sx={{
                    mt: 1,
                    maxHeight: 160,
                    overflowY: "scroll",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    pr: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Whole invoice FOC reason:
                  </Typography>
                  <Typography variant="body2">{wholeFocReason}</Typography>
                </Box>
              )}
              {focRequestMeta.status === "rejected" && focRequestMeta.decision_note && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    maxHeight: 96,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  Rejection reason: {focRequestMeta.decision_note}
                </Typography>
              )}
              {focRequestMeta.status === "approved" && focRequestMeta.order_id && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Approved — this invoice has already been created.
                </Typography>
              )}
              {focRequestMeta.status === "pending" && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Awaiting Chief Executive approval.
                </Typography>
              )}
            </Box>
          )}
          <Box
            sx={{
              mt: 3,
              backgroundColor: "#F5F6F8",
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 3,
              ...(isViewOnly && { pointerEvents: "none" }),
              // One consistent look for every field on the form.
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
                fontSize: 14,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D9DEE5" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B6C0CE" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0c54a0", borderWidth: "1.5px" },
                "&.Mui-disabled": { backgroundColor: "#F3F5F8" },
              },
              "& .MuiInputLabel-root": { fontSize: 14 },
              "& .MuiFormHelperText-root": { marginLeft: "2px", fontSize: 11.5 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
            {/* Order Details */}
            <Box
              sx={{
                border: "1px solid #E3E6EA",
                borderRadius: "14px",
                p: { xs: 2, sm: 3.5 },
                mb: 3,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: "#EEF2FF",
                    color: "#2F5FED",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Order Details
                </Typography>
              </Box>

            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Order Date"
                    value={
                      formik.values.orderDate
                        ? dayjs(formik.values.orderDate)
                        : null
                    }
                    inputFormat="DD-MM-YYYY"
                    // minDate={dayjs()}
                    // onChange={(date) => {
                    //   const formatted = date ? date.format("YYYY-MM-DD") : "";

                    //   formik.setFieldValue("orderDate", formatted);

                    //   // Recalculate due date if Credit method
                    //   if (
                    //     Number(formik.values.paymentMethod) === 4 &&
                    //     formik.values.creditDays
                    //   ) {
                    //     const newDueDate = dayjs(formatted)
                    //       .add(Number(formik.values.creditDays), "day")
                    //       .format("YYYY-MM-DD");

                    //     formik.setFieldValue("dueDate", newDueDate);
                    //   }
                    // }}
                    onChange={(date) => handleOrderDateChange(date)}
                    onClose={() => formik.setFieldTouched("orderDate", true)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        onBlur={() => formik.setFieldTouched("orderDate", true)}
                        error={
                          formik.touched.orderDate &&
                          Boolean(formik.errors.orderDate)
                        }
                        helperText={
                          formik.touched.orderDate && formik.errors.orderDate
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} lg={4} md={6} sx={{ minWidth: 0 }}>
                <Autocomplete
                  sx={{
                    minWidth: 0,
                    "& .MuiInputBase-root": { minWidth: 0 },
                    "& .MuiInputBase-input": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  options={customerList}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.company_name ? `${option.company_name} (${option.name})` : option.name}${option.phone_number ? ` - ${option.phone_number}` : ""}`
                      : ""
                  }
                  value={
                    customerList?.find(
                      (c) => c.customer_id == formik.values.customer_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleCustomerChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Customer"
                      variant="outlined"
                      error={
                        formik.touched.customer_id &&
                        Boolean(formik.errors.customer_id)
                      }
                      helperText={
                        (formik.touched.customer_id && formik.errors.customer_id) ||
                        "Not in the list? Leave this empty and type their details below — a new customer will be created automatically."
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {/* Only meaningful for a NEW customer typed below (no
                  customer_id) — an existing customer's type is fixed on
                  Customer Master and is only re-shown here for visibility
                  when picked from the dropdown above (handleCustomerChange
                  populates it). See [[Customers.jsx]] for the master form
                  this mirrors. Built as a fixed-height bordered box with a
                  floating label (matching the TextField/Autocomplete boxes
                  either side of it) instead of a caption-over-control stack —
                  a caption line above the control adds height the sibling
                  fields don't have, so the row stops lining up. */}
                <Box
                  sx={{
                    position: "relative",
                    height: 56,
                    border: "1px solid",
                    borderColor: "#D9DEE5",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    px: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: -9,
                      left: 10,
                      px: 0.5,
                      fontSize: 12,
                      bgcolor: "background.paper",
                      color: "text.secondary",
                    }}
                  >
                    Customer Type *
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    color="primary"
                    value={formik.values.customer_type}
                    onChange={(event, type) => {
                      if (!type) return;
                      formik.setFieldValue("customer_type", type);
                      if (type === "Individual") {
                        formik.setFieldValue("company_name", "");
                      }
                    }}
                    sx={{ height: 40 }}
                  >
                    <ToggleButton value="Company" sx={{ flex: 1 }}>Company</ToggleButton>
                    <ToggleButton value="Individual" sx={{ flex: 1 }}>Individual</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              {formik.values.customer_type === "Company" && (
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    name="company_name"
                    label="Company Name *"
                    placeholder="Enter registered company name"
                    value={formik.values.company_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                    helperText={formik.touched.company_name && formik.errors.company_name}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6} lg={4} sx={{ minWidth: 0 }}>
                {/* Without minWidth:0 a flex/grid item defaults to
                  shrink-to-fit (minWidth:auto) — a long value here (e.g. a
                  customer name used as the Bill To fallback) then ignores
                  the column's allotted width and overflows into/over the
                  "Select Customer" field to its left instead of truncating.
                  Same fix already applied to that Autocomplete above. */}
                <TextField
                  fullWidth
                  label="Bill To Company"
                  InputLabelProps={{ shrink: true }}
                  name="billToCompany"
                  value={formik.values.billToCompany}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Company / Bill To Name"
                  sx={{
                    minWidth: 0,
                    "& .MuiInputBase-root": { minWidth: 0 },
                    "& .MuiInputBase-input": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {/* "name" is the same tbl_customers column Customer Master
                  writes to, where it means "Contact Person Name" for a
                  Company and "Customer Name" for an Individual (see
                  [[Customers.jsx]]). Label/placeholder must switch the same
                  way here — otherwise a Company created from this form
                  stores its contact person under a field labeled "Customer
                  Name", which doesn't match how Customer Master itself
                  presents (or lets someone re-edit) that same record. */}
                <TextField
                  fullWidth
                  label={
                    formik.values.customer_type === "Company"
                      ? "Contact Person Name *"
                      : "Customer Name *"
                  }
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.replace(/\b\w/g, (char) =>
                      char.toUpperCase()
                    );
                    formik.setFieldValue("name", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  InputProps={{
                    style: { textTransform: "capitalize" },
                  }}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Customer Mobile"
                  name="phone_number"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone_number &&
                    Boolean(formik.errors.phone_number)
                  }
                  helperText={
                    formik.touched.phone_number && formik.errors.phone_number
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Customer Email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  name="emirateOption"
                  select
                  label="Select Emirate"
                  fullWidth
                  value={formik.values.emirateOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("emirateOption", value);
                    formik.setFieldValue(
                      "emirate",
                      value === "Others" ? "" : value
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                >
                  {emirates.map((emirate) => (
                    <MenuItem key={emirate.id} value={emirate.name}>
                      {emirate.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </Grid>

              {formik.values.emirateOption === "Others" && (
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    fullWidth
                    name="emirate"
                    label="Please specify Emirate/City/Country"
                    placeholder="Enter emirate, city or country"
                    value={formik.values.emirate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emirate && Boolean(formik.errors.emirate)
                    }
                    helperText={
                      formik.touched.emirate && formik.errors.emirate
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="P.O.Box"
                  name="po_box"
                  value={formik.values.po_box}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="VAT No."
                  name="vat_no"
                  value={formik.values.vat_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vat_no && Boolean(formik.errors.vat_no)}
                  helperText={formik.touched.vat_no && formik.errors.vat_no}
                />
              </Grid>

              <Grid item xs={12} lg={6} md={6}>
                <Autocomplete
                  // Only stock-holding stores (type "store" or "warehouse") can
                  // be the source of an invoice; salesman-type stores are excluded.
                  // store_type is a tbl_status id, so match on the joined name.
                  options={storeOptions.filter((s) => {
                    const t = (s.store_type_name || "").toLowerCase();
                    return t.includes("store") || t.includes("warehouse");
                  })}
                  getOptionLabel={(option) => option.store_name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    storeOptions.find((s) => s.id === formik.values.store_id) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    formik.setFieldValue(
                      "store_id",
                      newValue ? newValue.id : ""
                    );
                    // Fetching is left to the store_id effect (single source
                    // of truth) — calling fetchProductsWithStock here too
                    // used to fire a second, redundant request racing the
                    // effect's one; whichever landed last "won", which is
                    // exactly what made the product picker occasionally pop
                    // open (openOnFocus) with the wrong/previous store's
                    // stock the first time, only correcting itself once
                    // closed and reopened after both had settled.
                    const resetItems = formik.values.items.map((item) => ({
                      ...item,
                      productId: "",
                      sellingPrice: "",
                      quantity: 0,
                      subtotal: 0,
                      maxQuantity: 0,
                    }));
                    formik.setFieldValue("items", resetItems);
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {option.store_name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select a Store"
                      fullWidth
                      error={
                        formik.touched.store_id &&
                        Boolean(formik.errors.store_id)
                      }
                      helperText={
                        formik.touched.store_id && formik.errors.store_id
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: { maxHeight: 200, overflowY: "auto" },
                  }}
                />
              </Grid>
            </Grid>

            </Box>

            {/* Product Selection */}
            <Box
              sx={{
                border: "1px solid #E3E6EA",
                borderRadius: "14px",
                p: { xs: 2, sm: 3.5 },
                mb: 3,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: "#EEF2FF",
                    color: "#2F5FED",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Product Selection
                </Typography>
              </Box>
              <PillToggle
                value={selectionMode}
                onChange={handleModeChange}
                options={[
                  { value: "product", label: "Products" },
                  { value: "bundle", label: "Bundle" },
                ]}
              />
            </Box>

            {/* Discount Method / Tax Mode / Tax Rate — set these FIRST,
                before entering line items, since they change how the entry
                form behaves: Discount Method decides whether each row gets
                its own discount box or a single total discount applies, and
                Tax Mode decides whether "Incl. of Tax" figures show
                alongside every price as you type. Moved up from the Summary
                card so they're picked before, not after, filling in items. */}
            {/* Compact segmented controls instead of three long dropdowns —
                each of these is only ever a 2-way choice, so a pill toggle
                (same component as the Products/Bundle switch above) reads
                faster and takes a fraction of the width a <Select> needs. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                alignItems: "start",
                gap: 2.25,
                p: 2,
                mb: 2,
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #EEF2F7",
                // Small caps labels above each control.
                "& > div > .MuiTypography-caption": {
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.64rem",
                  fontWeight: 700,
                  color: "#94A3B8",
                  mb: "8px",
                },
              }}
            >
              {selectionMode === "product" && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5, fontWeight: 500 }}
                  >
                    Discount Method
                  </Typography>
                  <PillToggle soft
                    value={formik.values.discountType}
                    onChange={(mode) => {
                      // Convert the discount representation instead of
                      // wiping it — Grand Total must stay exactly the same
                      // across the switch. Critical on the edit page: this
                      // toggle must never blank out already-saved discount
                      // data just because it's being looked at differently.
                      const items = formik.values.items;
                      let updatedItems = items;
                      let newDocDiscount = formik.values.discount;
                      let newDocDiscountPercent = formik.values.discountPercent;

                      if (mode === "total") {
                        // Item-wise -> Total: fold every item's own
                        // discount into one Special Discount figure, then
                        // clear the item-level boxes.
                        const grossSubtotal = items.reduce((s, r) => {
                          if (r.fromBundle) return s;
                          return (
                            s +
                            (parseFloat(r.sellingPrice) || 0) *
                              (parseFloat(r.quantity) || 0)
                          );
                        }, 0);
                        const netSubtotal = items.reduce((s, r) => {
                          if (r.fromBundle) return s;
                          return s + (parseFloat(r.subtotal) || 0);
                        }, 0);
                        const totalItemDiscount = Math.max(
                          grossSubtotal - netSubtotal,
                          0
                        );
                        newDocDiscount = totalItemDiscount.toFixed(2);
                        newDocDiscountPercent =
                          grossSubtotal > 0
                            ? (
                                (totalItemDiscount / grossSubtotal) *
                                100
                              ).toFixed(2)
                            : "";
                        updatedItems = items.map((it) =>
                          it.fromBundle
                            ? it
                            : { ...it, discountValue: "", discountPercent: "" }
                        );
                      } else {
                        // Total -> Item-wise: spread the current Special
                        // Discount across items proportional to each
                        // line's own price × qty share.
                        const rawGrossTotal = items.reduce((s, r) => {
                          if (r.fromBundle || r.isFoc) return s;
                          return (
                            s +
                            (parseFloat(r.sellingPrice) || 0) *
                              (parseFloat(r.quantity) || 0)
                          );
                        }, 0);
                        const docDiscount =
                          parseFloat(formik.values.discount) || 0;
                        updatedItems = items.map((r) => {
                          if (r.fromBundle || r.isFoc || !r.productId) return r;
                          const gross =
                            (parseFloat(r.sellingPrice) || 0) *
                            (parseFloat(r.quantity) || 0);
                          const share =
                            rawGrossTotal > 0 ? gross / rawGrossTotal : 0;
                          const itemDiscount = docDiscount * share;
                          const pct =
                            gross > 0
                              ? ((itemDiscount / gross) * 100).toFixed(2)
                              : "";
                          return {
                            ...r,
                            discountUnit: "amount",
                            discountValue: itemDiscount.toFixed(2),
                            discountPercent: pct,
                          };
                        });
                        newDocDiscount = 0;
                        newDocDiscountPercent = "";
                      }

                      // recalcSummary's own setValues call replaces the
                      // whole values object wholesale, so any
                      // setFieldValue(...) issued BEFORE it here gets
                      // silently reverted (Formik resolves recalcSummary's
                      // update against its own stale pre-call snapshot).
                      // Issuing these AFTER instead makes them win.
                      recalcSummary(
                        updatedItems,
                        formik.values.shippingCharges,
                        newDocDiscount
                      );
                      formik.setFieldValue("discount", newDocDiscount);
                      formik.setFieldValue(
                        "discountPercent",
                        newDocDiscountPercent
                      );
                      formik.setFieldValue("discountType", mode);
                    }}
                    options={[
                      { value: "item", label: "Item-wise" },
                      { value: "total", label: "Total" },
                    ]}
                  />
                </Box>
              )}
              {/* Tax — Mode and Rate grouped together under one label, since
                  Rate is a sub-setting of Tax, not a peer of Discount
                  Method. Shown for FOC invoices too — VAT is charged on a FOC
                  item's value (deemed supply), so the Tax Mode and Tax Rate
                  still apply and must stay selectable. */}
              {(
                <>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.4,
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Tax Mode
                      {/* Explains what the mode actually DOES to the unit
                          prices below — on hover/tap only, so it doesn't
                          cost permanent space the way a persistent banner
                          did. The two modes read the same product prices
                          completely differently (tax added on top vs.
                          already baked in), which is exactly the part
                          people get tripped up on, so it's still worth
                          surfacing — just as a tooltip, not a fixture. */}
                      <Tooltip
                        arrow
                        title={
                          (formik.values.taxMode || "exclusive") === "exclusive"
                            ? "Each product's Unit Price below is before VAT. Tax gets added on top when totaling up, so the Grand Total ends up higher than the prices you entered."
                            : "Each product's Unit Price below is treated as already including VAT. Tax is worked back out of it for the Subtotal/Tax breakdown, but the Grand Total stays the same as what the prices add up to."
                        }
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                        />
                      </Tooltip>
                    </Typography>
                    <PillToggle soft
                      value={formik.values.taxMode || "exclusive"}
                      onChange={(mode) => {
                        // Preserve whatever's already there — just read the
                        // SAME unit prices under the new mode's math.
                        // Critical on the edit page: switching modes must
                        // never blank out already-saved discount data.
                        // Exclusive reads each row's price as pre-tax (tax
                        // added on top); Inclusive reads it as already
                        // containing tax (tax extracted back out) — see
                        // recalcSummary's own branch for the actual math.
                        recalcSummary(
                          formik.values.items,
                          undefined,
                          formik.values.discount,
                          formik.values.taxType,
                          undefined,
                          mode
                        );
                        formik.setFieldValue("taxMode", mode);
                      }}
                      options={[
                        { value: "exclusive", label: "Exclusive of Tax" },
                        { value: "inclusive", label: "Inclusive of Tax" },
                      ]}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5, fontWeight: 500 }}
                    >
                      Tax Rate
                    </Typography>
                    <PillToggle soft
                      value={String(formik.values.taxType)}
                      onChange={(rate) => {
                        // Same reasoning as Discount Method / Tax Mode above
                        // — changing the tax rate shifts every downstream
                        // figure (discount %, Subtotal after Discount), so
                        // wipe them back to a clean slate rather than
                        // recompute on top of a now-stale discount.
                        const clearedItems = formik.values.items.map((row) =>
                          row.fromBundle
                            ? row
                            : { ...row, discountValue: "", discountPercent: "" }
                        );
                        recalcSummary(
                          clearedItems,
                          undefined,
                          0,
                          rate,
                          undefined,
                          formik.values.taxMode
                        );
                        formik.setFieldValue("discount", 0);
                        formik.setFieldValue("discountPercent", "");
                        formik.setFieldValue("taxType", rate);
                      }}
                      options={[
                        { value: "5", label: "5%" },
                        { value: "0", label: "0%" },
                      ]}
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* FOC (Item 8) — whole-invoice shortcut. Not shown while
                editing (FOC can only be set through the request/approval
                flow, never toggled on an existing invoice) or in Bundle mode
                (bundle rows don't carry a FOC checkbox — see the per-row
                note below). */}
            {!id && selectionMode === "product" && (
              <Box sx={{ mb: 1.5 }}>
                {!bulkFocOpen ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CardGiftcardOutlinedIcon fontSize="small" />}
                    onClick={() => setBulkFocOpen(true)}
                    sx={{
                      textTransform: "none",
                      color: "#0c54a0",
                      borderColor: "#B8D7FF",
                      backgroundColor: "#EEF4FF",
                      fontWeight: 600,
                      borderRadius: 2,
                      "&:hover": { backgroundColor: "#E1ECFF", borderColor: "#8FBBFF" },
                    }}
                  >
                    Mark entire invoice as FOC / Complimentary
                  </Button>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: 2,
                      backgroundColor: "#EEF4FF",
                      border: "1px solid #B8D7FF",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#0c54a0" }}>
                      Apply FOC to every item:
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Shared reason (optional) — e.g. complimentary gift order"
                      value={bulkFocReason}
                      onChange={(e) => setBulkFocReason(e.target.value)}
                      sx={{ flex: 1, minWidth: 220, backgroundColor: "#fff" }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        // Discount is left as-is, same as the per-row FOC
                        // checkbox — FOC just means this line isn't billed,
                        // the price/discount fields stay live so the record
                        // still shows what it would have cost.
                        const updated = formik.values.items.map((row) =>
                          !row.fromBundle && row.productId
                            ? { ...row, isFoc: true, focReason: bulkFocReason }
                            : row
                        );
                        recalcSummary(updated);
                        formik.setFieldValue("wholeFocReason", bulkFocReason);
                        setBulkFocOpen(false);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Apply to all
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setBulkFocOpen(false)}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Table Header */}
            <Grid
              container
              spacing={1}
              sx={{
                backgroundColor: "#F8FAFC",
                border: "1px solid #EEF2F7",
                borderRadius: "10px",
                mt: 1,
                mb: 1,
                py: 0.5,
                ml: 0,
                width: "100%",
                "& .MuiTypography-root": {
                    lineHeight: 1.35,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.64rem",
                  fontWeight: 700,
                  color: "#64748B",
                },
                display: {
                  xs: "none",
                  // Bundle mode never uses these generic columns — the
                  // picker is a single full-width field, and a selected
                  // bundle renders as its own card with its own labels — so
                  // hide this header for the whole mode, not just once a
                  // bundle has actually been picked.
                  md:
                    selectionMode === "bundle" ||
                    formik.values.items.some((r) => r.fromBundle)
                      ? "none"
                      : "flex",
                },
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Grid item md={2.3}>
                <Typography variant="caption" fontWeight={600}>
                  Product Name / SKU
                </Typography>
              </Grid>
              <Grid item md={1.4}>
                <Typography variant="caption" fontWeight={600}>
                  Unit Price
                    <Box component="span" sx={{ display: "block", fontWeight: 500, opacity: 0.8 }}>
                      {formik.values.taxMode === "inclusive" ? "(Incl. VAT)" : "(Excl. VAT)"}
                    </Box>
                </Typography>
              </Grid>
              <Grid item md={1.1}>
                <Typography variant="caption" fontWeight={600}>
                  Quantity
                </Typography>
              </Grid>
              <Grid item md={1.5}>
                <Typography variant="caption" fontWeight={600}>
                  Subtotal
                </Typography>
              </Grid>
              <Grid item md={3}>
                <Typography variant="caption" fontWeight={600}>
                  Discount (Amount or %)
                </Typography>
              </Grid>
              <Grid item md={1.5}>
                <Typography variant="caption" fontWeight={600}>
                  After Discount
                </Typography>
              </Grid>
              <Grid item md={1.2} />
            </Grid>

            {/* Dynamic Items */}
            {formik.values.items.map((row, idx) => {
              const previousRow = formik.values.items[idx - 1];
              const nextRow = formik.values.items[idx + 1];
              const showBundleHeader =
                row.fromBundle &&
                (!previousRow?.fromBundle ||
                  previousRow.bundleGroupId !== row.bundleGroupId);
              const isLastBundleRow =
                row.fromBundle &&
                (!nextRow?.fromBundle ||
                  nextRow.bundleGroupId !== row.bundleGroupId);

              return (
              <Fragment key={idx}>
                  {showBundleHeader && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          px: 1.5,
                          py: 1,
                          border: "1px solid #B8D7FF",
                          borderBottom: "none",
                          borderRadius: "8px 8px 0 0",
                          backgroundColor: "#EEF4FF",
                        }}
                      >
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="#0c54a0"
                            noWrap
                          >
                            Bundle: {row.bundleName || "Selected Bundle"}
                          </Typography>
                          {row.bundleSku ? (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                              noWrap
                            >
                              SKU: {row.bundleSku} • {formik.values.items.filter(i => i.bundleGroupId === row.bundleGroupId).length} items
                            </Typography>
                          ) : null}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          {(() => {
                            const grp = formik.values.items.filter(
                              (it) => it.bundleGroupId === row.bundleGroupId
                            );
                            const groupGross = grp.reduce(
                              (s, it) => s + (parseFloat(it.sellingPrice || 0) * (parseFloat(it.quantity || 0) || 0)),
                              0
                            );
                            const mode = row.discountMode || "bundle";
                            const groupNet =
                              mode === "item"
                                ? grp.reduce((s, it) => {
                                    const gross =
                                      parseFloat(it.sellingPrice || 0) *
                                      (parseFloat(it.quantity || 0) || 0);
                                    const itDisc = discountAmountFromPercent(
                                      gross,
                                      it.discountPercent
                                    );
                                    return s + (gross - itDisc);
                                  }, 0)
                                : Math.max(
                                    groupGross -
                                      discountAmountFromPercent(groupGross, row.bundleDiscount),
                                    0
                                  );
                            const discAmt = groupGross - groupNet;

                            return (
                              <Box sx={{ textAlign: "right", minWidth: 200 }}>
                                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                                  Subtotal: {formatAED(groupGross)}
                                </Box>
                                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                                  - Discount: {formatAED(discAmt)}
                                </Box>
                                <Box sx={{ fontSize: 14, fontWeight: 700, color: "#0c54a0" }}>
                                  Total: {formatAED(groupNet)}
                                </Box>
                              </Box>
                            );
                          })()}

                          <TextField
                            select
                            size="small"
                            label="Discount type"
                            value={row.discountMode || "bundle"}
                            onChange={(e) => {
                              const modeVal = e.target.value;
                              const updatedItems = formik.values.items.map(
                                (item) =>
                                  item.bundleGroupId === row.bundleGroupId
                                    ? { ...item, discountMode: modeVal }
                                    : item
                              );
                              formik.setFieldValue("items", updatedItems);
                              recalcSummary(updatedItems);
                            }}
                            sx={{ width: 200 }}
                          >
                            <MenuItem value="bundle">Bundle Discount %</MenuItem>
                            <MenuItem value="item">Per Item Discount %</MenuItem>
                          </TextField>

                          {(row.discountMode || "bundle") === "bundle" && (
                          <TextField
                            size="small"
                            label="Bundle Discount %"
                            value={row.bundleDiscount ?? ""}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const value = sanitizePercentInput(e.target.value);
                              const updatedItems = formik.values.items.map((item) =>
                                item.bundleGroupId === row.bundleGroupId
                                  ? { ...item, bundleDiscount: value }
                                  : item
                              );
                              formik.setFieldValue("items", updatedItems);
                              recalcSummary(updatedItems);
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ inputMode: "decimal", max: 100, min: 0 }}
                            sx={{ width: 160 }}
                          />
                          )}

                          <Tooltip title="Remove this bundle" arrow>
                            <IconButton
                              size="small"
                              sx={{ color: "#c62828", flexShrink: 0 }}
                              onClick={() => {
                                const items = formik.values.items;
                                const key = (it) =>
                                  it?.bundleGroupId ?? `bid-${it?.bundleId}`;
                                const groupKey = key(row);
                                // idx is this bundle's header (first) row — remove
                                // only its contiguous rows, not every bundle.
                                let end = idx;
                                while (
                                  end + 1 < items.length &&
                                  items[end + 1].fromBundle &&
                                  key(items[end + 1]) === groupKey
                                ) {
                                  end++;
                                }
                                const kept = [];
                                const keptSearch = [];
                                items.forEach((it, i) => {
                                  if (i < idx || i > end) {
                                    kept.push(it);
                                    keptSearch.push(searchTerm[i] ?? "");
                                  }
                                });
                                const finalItems = kept.length
                                  ? kept
                                  : [
                                      {
                                        productId: "",
                                        sellingPrice: 0,
                                        quantity: 0,
                                        subtotal: 0,
                                      },
                                    ];
                                formik.setFieldValue("items", finalItems);
                                setSearchTerm(kept.length ? keptSearch : [""]);
                                recalcSummary(finalItems);
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          borderLeft: "1px solid #B8D7FF",
                          borderRight: "1px solid #B8D7FF",
                          backgroundColor: "#fff",
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          🔒 Discounts are for internal records only — they are not
                          shown on the printed invoice; the customer
                          only sees the reduced prices.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: { xs: "none", md: "block" },
                          borderLeft: "1px solid #B8D7FF",
                          borderRight: "1px solid #B8D7FF",
                          backgroundColor: "#fff",
                          px: 1.5,
                          pt: 1,
                        }}
                      >
                        <Grid container spacing={1.5} sx={{ mx: 0, width: "100%" }}>
                          <Grid item md={2.5}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              Product Name / SKU
                            </Typography>
                          </Grid>
                          <Grid item md={1.4}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              {formik.values.taxMode === "inclusive"
                    ? "Unit Price (Incl. VAT)"
                    : "Unit Price (Excl. VAT)"}
                            </Typography>
                          </Grid>
                          <Grid item md={1.5}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              Quantity
                            </Typography>
                          </Grid>
                          <Grid item md={1.2}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              Subtotal
                            </Typography>
                          </Grid>
                          <Grid item md={3.3}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              Discount (AED / %)
                            </Typography>
                          </Grid>
                          <Grid item md={1.6}>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              Subtotal after Discount
                            </Typography>
                          </Grid>
                          <Grid item md={0.5} />
                        </Grid>
                      </Box>
                    </Grid>
                )}
              {(() => {
              // Plain, already-selected product rows get enclosed in their
              // own card with the FOC control as a footer strip (Item 8) —
              // bundle sub-rows and the not-yet-picked empty row keep the
              // old flat layout, they already have (or don't need) their
              // own visual grouping.
              const isFocCard = !row.fromBundle && !!row.productId;
              const rowFieldsGrid = (
              <Grid
                container
                spacing={1.5}
                alignItems="center"
                sx={{
                  mt: row.fromBundle || isFocCard ? 0 : 1,
                  // The "FOC — not billed" caption under Subtotal after
                  // Discount is floated absolutely (out of flow, so it
                  // doesn't grow just that one column taller than its
                  // siblings) — reserve the room it needs here instead, on
                  // the row as a whole, so it doesn't overlap the FOC
                  // footer strip directly below.
                  pb: row.isFoc ? 2.5 : 0,
                  ...(row.fromBundle && {
                    mx: 0,
                    width: "100%",
                    px: 1.5,
                    py: 1.25,
                    backgroundColor: "#fff",
                    borderLeft: "1px solid #B8D7FF",
                    borderRight: "1px solid #B8D7FF",
                    borderBottom: "1px solid #E5F0FF",
                    borderRadius: 0,
                  }),
                }}
              >
                {/* Product Name / SKU — full width while picking a bundle
                    (selected bundle explodes into its own card layout, and
                    every other column below is hidden until then anyway, so
                    there's no reason to squeeze the picker into 2.5/12). */}
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={selectionMode === "bundle" && !row.productId ? 11 : 2.3}
                  sx={{ position: "relative" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Product Name / SKU
                  </Typography>
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />
                  {selectionMode === "bundle" && !row.productId ? (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Autocomplete
                        key={`bundle-select-${idx}`}
                        fullWidth
                        openOnFocus
                        disabled={!formik.values.store_id || productListLoading}
                        options={bundleList}
                        getOptionLabel={(option) =>
                          option.product_name
                            ? `${option.product_name} (SKU: ${option.sku || ""})`
                            : ""
                        }
                        getOptionDisabled={(option) => !option.eligible}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={getBundleForRow(row)}
                        onChange={(e, newValue) =>
                          handleBundleSelect(idx, newValue)
                        }
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={option.id}
                            style={{
                              display: "block",
                              padding: "8px 12px",
                              borderBottom: "1px solid #eee",
                              opacity: option.eligible ? 1 : 0.6,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 8,
                              }}
                            >
                              <span style={{ fontWeight: 500, color: "#333" }}>
                                {option.product_name}{" "}
                                <span
                                  style={{ color: "#555", fontWeight: 400 }}
                                >
                                  (SKU: {option.sku || "-"})
                                </span>
                              </span>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: "#0c54a0",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatAED(getBundleStorePrice(option))}
                              </span>
                            </div>
                            <div
                              style={{
                                marginTop: 4,
                                display: "grid",
                                gap: 2,
                              }}
                            >
                              {(option.components || []).slice(0, 2).map((c) => (
                                <div
                                  key={c.product_id}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    gap: 8,
                                    fontSize: 12,
                                    color:
                                      (c.available ?? 0) >= c.required_per_bundle
                                        ? "#2e7d32"
                                        : "#c62828",
                                  }}
                                >
                                  {/* min-width:0 lets this truncate instead of
                                      wrapping "x N" onto its own line, which
                                      used to land right under "Stock: N" and
                                      read as two different stock numbers. */}
                                  <span
                                    style={{
                                      minWidth: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {c.product_name}
                                    {c.sku ? ` (SKU: ${c.sku})` : ""}
                                  </span>
                                  <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                                    Needs {c.required_per_bundle} · Stock:{" "}
                                    {c.available ?? 0}
                                  </span>
                                </div>
                              ))}
                              {(option.components || []).length > 2 && (
                                <Typography
                                  component="div"
                                  variant="caption"
                                  sx={{ color: "text.secondary" }}
                                >
                                  +{(option.components || []).length - 2} more
                                  product(s)
                                </Typography>
                              )}
                            </div>
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                color: option.eligible ? "#2e7d32" : "#c62828",
                              }}
                            >
                              {option.eligible
                                ? `Available: ${option.max_qty} bundle(s)`
                                : option.not_yet_started
                                ? `Not available yet — starts ${dayjs(option.valid_from).format("DD-MM-YYYY")}`
                                : option.is_expired
                                ? `Expired on ${dayjs(option.valid_to).format("DD-MM-YYYY")}`
                                : "Out of stock in this store"}
                            </div>
                            {/* "Check other stores" only helps for a stock
                                problem — a scheduling block applies
                                everywhere, so skip it for those. */}
                            {!option.eligible && !option.not_yet_started && !option.is_expired && (
                              <div style={{ marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const info = bundleStoreAvailability[option.id];
                                  if (!info) {
                                    return (
                                      <span
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          checkBundleOtherStores(option.id);
                                        }}
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#0c54a0",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                        }}
                                      >
                                        Check other stores
                                      </span>
                                    );
                                  }
                                  if (info.loading) {
                                    return (
                                      <span style={{ fontSize: 12, color: "#666" }}>
                                        Checking other stores…
                                      </span>
                                    );
                                  }
                                  if (!info.byStore.length) {
                                    return (
                                      <span style={{ fontSize: 12, color: "#c62828" }}>
                                        Out of stock in every store
                                      </span>
                                    );
                                  }
                                  return (
                                    <span style={{ fontSize: 12, color: "#2e7d32", fontWeight: 600 }}>
                                      Available at:{" "}
                                      {info.byStore
                                        .map((s) => `${s.store_name} (${s.max_qty})`)
                                        .join(" · ")}
                                    </span>
                                  );
                                })()}
                              </div>
                            )}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              formik.values.store_id
                                ? "Select a Bundle"
                                : "Select a store first"
                            }
                            fullWidth
                            onBlur={() =>
                              formik.setFieldTouched(
                                `items[${idx}].productId`,
                                true
                              )
                            }
                            error={
                              formik.touched.items?.[idx]?.productId &&
                              Boolean(formik.errors.items?.[idx]?.productId)
                            }
                            helperText={
                              formik.touched.items?.[idx]?.productId &&
                              formik.errors.items?.[idx]?.productId
                            }
                          />
                        )}
                        ListboxProps={{
                          style: { maxHeight: 260, overflowY: "auto" },
                        }}
                      />
                      <Tooltip title="View bundle details">
                        <span>
                          <IconButton
                            sx={{ mt: 1 }}
                            disabled={!getBundleForRow(row)}
                            onClick={() => setBundleDetails(getBundleForRow(row))}
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                  <Autocomplete
                    key={`product-select-${idx}`}
                    fullWidth
                    openOnFocus
                    disabled={
                      !formik.values.store_id || row.fromBundle || productListLoading
                    }
                    // In-stock products first, out-of-stock pushed to the
                    // bottom — Array.prototype.sort is stable, so within each
                    // group the original (search-relevance) order from
                    // fetchProductsWithStock is preserved.
                    options={[
                      ...(productList || []),
                      ...(row.productId &&
                      !productList.some((p) => p.id === row.productId)
                        ? [{ ...row, id: row.productId }]
                        : []),
                    ].sort((a, b) => Number(b.inStock) - Number(a.inStock))}
                    getOptionLabel={(option) =>
                      option.product_name
                        ? `${option.product_name} ${option.sku || ""}`
                        : ""
                    }
                    getOptionDisabled={(option) => !option.inStock}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      productList.find((p) => p.id === row.productId) || null
                    }
                    inputValue={searchTerm[idx]}
                    onInputChange={(e, value, reason) => {
                      const updated = [...searchTerm];
                      updated[idx] = value;
                      setSearchTerm(updated);

                      // Only search on typing
                      if (reason === "input") {
                        debounce(() => {
                          fetchProductsWithStock(
                            formik.values.store_id || null,
                            value
                          );
                        });
                      }

                      // Clear search
                      if (reason === "clear" || reason === "reset") {
                        fetchProductsWithStock(
                          formik.values.store_id || null,
                          ""
                        );
                      }
                    }}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleProductSelect(idx, newValue);
                      } else {
                        const updatedItems = [...formik.values.items];
                        updatedItems[idx] = {
                          ...updatedItems[idx],
                          productId: null,
                          isBundle: false,
                          bundleId: null,
                          sellingPrice: 0,
                          quantity: 0,
                          subtotal: 0,
                        };
                        formik.setFieldValue("items", updatedItems);
                        recalcSummary(updatedItems);
                      }
                    }}
                    renderOption={(props, option, { selected }) => {
                      const size = option.size || "NA";
                      const color = option.color || "NA";

                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "6px 10px",
                            gap: 10,
                            borderBottom: "1px solid #eee",
                            backgroundColor: selected ? "#e3f2fd" : "#fff",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: 14,
                          }}
                        >
                          {/* Product Image */}
                          <img
                            src={option.product_image}
                            alt={option.product_name}
                            style={{
                              width: 36,
                              height: 36,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ccc",
                            }}
                          />

                          {/* Product Details */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              lineHeight: 1.2,
                            }}
                          >
                            <span style={{ fontWeight: 500, color: "#333" }}>
                              {option.product_name}{" "}
                              <span style={{ color: "#555", fontWeight: 400 }}>
                                {" "}
                                (SKU: {option.sku})
                              </span>
                            </span>

                            <span style={{ fontSize: 12, color: "#007BFF" }}>
                              Size: {size}, Colour: {color}
                            </span>

                            <span
                              style={{
                                color: option.inStock ? "green" : "red",
                                fontWeight: 500,
                                fontSize: 12,
                              }}
                            >
                              {option.inStock
                                ? option.reserved > 0
                                  ? `Available: ${
                                      option.atp ?? option.quantity
                                    } · Reserved: ${
                                      option.reserved
                                    } · In Stock: ${option.quantity}${
                                      option.expired > 0 ? ` · ${option.expired} expired` : ""
                                    }`
                                  : `In Stock: ${option.quantity}${
                                      option.expired > 0 ? ` · ${option.expired} expired` : ""
                                    }`
                                : option.expired > 0
                                ? `Expired: ${option.expired} unit${option.expired === 1 ? "" : "s"} past expiry — not sellable${
                                    option.reserved > 0
                                      ? ` · ${option.reserved} reserved`
                                      : ""
                                  }`
                                : `Out of Stock${
                                    option.reserved > 0
                                      ? ` · ${option.reserved} reserved`
                                      : ""
                                  }`}
                            </span>
                          </div>
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const selectedProduct = productList.find(
                        (p) => p.id === row.productId
                      );

                      return (
                        <TextField
                          {...params}
                          size="small"
                          label={
                            selectedProduct
                              ? ""
                              : productListLoading
                              ? "Loading products…"
                              : formik.values.store_id
                              ? "Select a Product"
                              : "Select a store first"
                          }
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: selectedProduct
                                ? "none"
                                : "1px solid #E3E6EA",
                            },
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: selectedProduct
                                ? "transparent"
                                : "#FCFCFD",
                            },
                          }}
                          onBlur={() =>
                            formik.setFieldTouched(
                              `items[${idx}].productId`,
                              true
                            )
                          }
                          error={
                            formik.touched.items?.[idx]?.productId &&
                            Boolean(formik.errors.items?.[idx]?.productId)
                          }
                          helperText={
                            formik.touched.items?.[idx]?.productId &&
                            formik.errors.items?.[idx]?.productId
                          }
                          FormHelperTextProps={{
                            sx: { position: "absolute", top: "100%", left: 0, m: 0, whiteSpace: "nowrap" },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedProduct ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  paddingLeft: 2,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <img
                                  src={selectedProduct.product_image}
                                  alt={selectedProduct.product_name}
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    flexShrink: 0,
                                  }}
                                />

                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: 0,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: "#101828",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {selectedProduct.product_name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#2F5FED",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {selectedProduct.sku} ·{" "}
                                    {selectedProduct.size || "NA"},{" "}
                                    {selectedProduct.color || "NA"}
                                  </span>
                                </div>
                              </div>
                            ) : null,

                            endAdornment: (
                              <>
                                {!formik.values.items[idx].productId &&
                                  searchTerm[idx] && (
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const updated = [...searchTerm];
                                        updated[idx] = "";
                                        setSearchTerm(updated);
                                        fetchProductsWithStock(
                                          formik.values.store_id || null,
                                          ""
                                        );
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            value: selectedProduct
                              ? ""
                              : params.inputProps.value,
                            style: selectedProduct
                              ? { flexGrow: 0, width: 0, minWidth: 0, padding: 0 }
                              : params.inputProps.style,
                          }}
                        />
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: 200, overflowY: "auto" },
                    }}
                  />
                  )}
                </Grid>

                {/* Unit Price (Exclusive of VAT) — collapsed (not just
                    hidden) while picking a bundle, so it doesn't reserve
                    space the picker above could use. */}
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={1.4}
                  sx={{
                    position: "relative",
                    display:
                      selectionMode === "bundle" && !row.productId
                        ? "none"
                        : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    {formik.values.taxMode === "inclusive"
                    ? "Unit Price (Incl. VAT)"
                    : "Unit Price (Excl. VAT)"}
                  </Typography>
                  {/* Empty fixed-height slot — kept (not removed) so this
                      column still lines up with the matching reserved slot
                      in every sibling column below. Unit Price never shows
                      tax math here now; that's Summary-only. */}
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    // ?? not || — a genuine 0 price must still render as
                    // "0", not blank (0 is falsy, so || was hiding it).
                    value={row.sellingPrice ?? ""}
                    onChange={(e) => {
                      const value = Math.min(
                        MAX_MONEY_VALUE,
                        parseFloat(e.target.value) || 0
                      );
                      const updated = [...formik.values.items];
                      updated[idx] = {
                        ...updated[idx],
                        sellingPrice: value,
                        subtotal: (
                          value * (parseFloat(updated[idx].quantity) || 0)
                        ).toFixed(2),
                      };
                      formik.setFieldValue("items", updated);
                      recalcSummary(updated);
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={() =>
                      formik.setFieldTouched(`items[${idx}].sellingPrice`, true)
                    }
                    error={
                      formik.touched.items?.[idx]?.sellingPrice &&
                      Boolean(formik.errors.items?.[idx]?.sellingPrice)
                    }
                    helperText={
                      formik.touched.items?.[idx]?.sellingPrice &&
                      formik.errors.items?.[idx]?.sellingPrice
                    }
                    // Floated out of normal flow — a validation message
                    // here would otherwise grow ONLY this row's height
                    // (Subtotal/Discount/etc. never show one), making rows
                    // with an error look taller than the ones around them.
                    FormHelperTextProps={{
                      sx: { position: "absolute", top: "100%", left: 0, m: 0, whiteSpace: "nowrap" },
                    }}
                  />
                </Grid>

                {/* Quantity */}
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={1.1}
                  sx={{
                    position: "relative",
                    display:
                      selectionMode === "bundle" && !row.productId
                        ? "none"
                        : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Quantity
                  </Typography>
                  {/* Empty slot matching the Unit Price column's "Incl. VAT"
                      caption height, so this box lines up with it either
                      way. */}
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: 40,
                      border: "1px solid",
                      borderColor:
                        formik.touched.items?.[idx]?.quantity &&
                        formik.errors.items?.[idx]?.quantity
                          ? "error.main"
                          : "#d8dee6",
                      borderRadius: 1,
                      overflow: "hidden",
                      backgroundColor: "#FCFCFD",
                      width: "100%",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(
                          idx,
                          String(Math.max((Number(row.quantity) || 0) - 1, 0))
                        )
                      }
                      sx={{ borderRadius: 0, width: 28, height: "100%", flexShrink: 0, px: 0 }}
                    >
                      <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                        −
                      </Typography>
                    </IconButton>
                    <input
                      value={row.quantity || ""}
                      onChange={(e) => handleQuantityChange(idx, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onBlur={() =>
                        formik.setFieldTouched(`items[${idx}].quantity`, true)
                      }
                      inputMode="numeric"
                      style={{
                        flex: 1,
                        width: 0,
                        minWidth: 20,
                        textAlign: "center",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "inherit",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(
                          idx,
                          String((Number(row.quantity) || 0) + 1)
                        )
                      }
                      sx={{ borderRadius: 0, width: 28, height: "100%", flexShrink: 0, px: 0 }}
                    >
                      <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                        +
                      </Typography>
                    </IconButton>
                  </Box>
                  {formik.touched.items?.[idx]?.quantity &&
                    formik.errors.items?.[idx]?.quantity && (
                      // Floated out of normal flow — same reasoning as the
                      // Unit Price field's helper text: shouldn't grow only
                      // this row's height relative to its neighbors.
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 4,
                          mt: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formik.errors.items?.[idx]?.quantity}
                      </Typography>
                    )}
                </Grid>

                {/* Subtotal (gross — selling price × quantity, before any discount) */}
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={1.5}
                  sx={{
                    display:
                      selectionMode === "bundle" && !row.productId
                        ? "none"
                        : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Subtotal
                  </Typography>
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />
                  <Box
                    sx={{
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: "#F7F8FA",
                      border: "1px dashed #cbd1d9",
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {formatAED(
                      (parseFloat(row.sellingPrice) || 0) *
                        (parseFloat(row.quantity) || 0)
                    )}
                  </Box>
                </Grid>

                {/* Discount (value) — a bundle-mode sub-row has no field here,
                    its discount is entered once on the bundle header instead. */}
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  sx={{
                    position: "relative",
                    display:
                      selectionMode === "bundle" && !row.productId
                        ? "none"
                        : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    Discount (Amount or %)
                  </Typography>
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />
                  {/* FOC rows keep the discount box enabled too — purely
                      informational (the item is billed at 0 regardless),
                      but the user can still record what discount would
                      have applied instead of it just reading "—". */}
                  {((row.fromBundle && (row.discountMode || "bundle") === "item") ||
                    (!row.fromBundle && row.productId)) ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        height: 40,
                        border: "1px solid #d8dee6",
                        borderRadius: 1,
                        overflow: "hidden",
                        backgroundColor: "#FCFCFD",
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder="0.00"
                        disabled={
                          selectionMode === "product" &&
                          formik.values.discountType === "total"
                        }
                        value={row.discountValue ?? ""}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const gross =
                            (parseFloat(row.sellingPrice) || 0) *
                            (parseFloat(row.quantity) || 0);
                          const valStr = sanitizeMoneyInput(
                            e.target.value,
                            gross > 0 ? gross : MAX_MONEY_VALUE
                          );
                          const numVal = parseFloat(valStr) || 0;
                          const pct = gross > 0 ? ((numVal / gross) * 100).toFixed(2) : "";

                          if (formik.values.discountType !== "item") {
                            formik.setFieldValue("discountType", "item");
                            formik.setFieldValue("discount", 0);
                            formik.setFieldValue("discountPercent", 0);
                          }

                          const updated = [...formik.values.items];
                          updated[idx] = {
                            ...updated[idx],
                            discountUnit: "amount",
                            discountValue: valStr,
                            discountPercent: pct,
                          };
                          recalcSummary(updated);
                        }}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          endAdornment: (
                            <InputAdornment position="end" sx={{ typography: "caption", color: "text.secondary", fontWeight: 600 }}>
                              AED
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
                        sx={{ flex: 1.2, minWidth: 70, px: 1.5, "& .MuiInputBase-root": { height: "100%" } }}
                      />
                      <Divider orientation="vertical" flexItem />
                      <TextField
                        size="small"
                        placeholder="0"
                        disabled={
                          selectionMode === "product" &&
                          formik.values.discountType === "total"
                        }
                        value={row.discountPercent ?? ""}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const gross =
                            (parseFloat(row.sellingPrice) || 0) *
                            (parseFloat(row.quantity) || 0);
                          const pctStr = sanitizePercentInput(e.target.value);
                          const pctNum = parseFloat(pctStr) || 0;
                          const val = gross > 0 ? ((gross * pctNum) / 100).toFixed(2) : "";

                          if (formik.values.discountType !== "item") {
                            formik.setFieldValue("discountType", "item");
                            formik.setFieldValue("discount", 0);
                            formik.setFieldValue("discountPercent", 0);
                          }

                          const updated = [...formik.values.items];
                          updated[idx] = {
                            ...updated[idx],
                            discountUnit: "percent",
                            discountPercent: pctStr,
                            discountValue: val,
                          };
                          recalcSummary(updated);
                        }}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          endAdornment: (
                            <InputAdornment position="end" sx={{ typography: "caption", color: "text.secondary", fontWeight: 600 }}>
                              %
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
                        sx={{ flex: 1, minWidth: 55, px: 1.5, "& .MuiInputBase-root": { height: "100%" } }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: "#F7F8FA",
                        border: "1px dashed #cbd1d9",
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        fontSize: 14,
                        color: "text.disabled",
                      }}
                    >
                      —
                    </Box>
                  )}
                </Grid>

                {/* Subtotal after Discount (net — what's actually billed) */}
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={1.5}
                  sx={{
                    position: "relative",
                    display:
                      selectionMode === "bundle" && !row.productId
                        ? "none"
                        : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontWeight: 500,
                    }}
                  >
                    Subtotal after Discount
                  </Typography>
                  <Box sx={{ height: { xs: 0, md: 22 }, mb: { xs: 0, md: 0.5 } }} />
                  <Box
                    sx={{
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: "#EEF4FF",
                      border: "1px dashed #B8D7FF",
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0c54a0",
                    }}
                  >
                    {row.isFoc
                      ? // FOC: show what the line would have cost (focValue)
                        // instead of blanking it to 0.00 — the price and
                        // discount still read normally, only the actual
                        // charge (Grand Total) comes out to 0.
                        formatAED(row.focValue)
                      : formatAED(row.subtotal)}
                  </Box>
                  {row.isFoc && (
                    // Floated out of normal flow — same reasoning as the
                    // validation error text: shouldn't grow only this
                    // column's height relative to Discount/Quantity/etc.
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        mt: 0.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      FOC — not billed
                    </Typography>
                  )}
                </Grid>

                {/* Delete Button */}
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={1.2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {!row.fromBundle && (
                    <IconButton
                      onClick={() => removeRow(idx)}
                      disabled={formik.values.items.length === 1}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  )}
                </Grid>

                </Grid>
              );

              // FOC / Complimentary (Item 8) footer — plain products only; a
              // bundle's own discount math already folds too many things
              // together to also carry a per-component FOC flag cleanly.
              // Disabled on edit: a FOC line can only be created through the
              // request → Chief Executive approval flow, never toggled after
              // the fact on an existing invoice.
              const focFooter = isFocCard ? (
                <Box
                  sx={{
                    borderTop: "1px solid",
                    borderColor: row.isFoc ? "#B8D7FF" : "#E3E6EA",
                    backgroundColor: row.isFoc ? "#EEF4FF" : "#FAFBFC",
                    px: { xs: 1.5, md: 2 },
                    py: 1,
                  }}
                >
                      <Box
                        // Row-level click expands/collapses the reason —
                        // deliberately NOT wrapped in FormControlLabel, whose
                        // whole-label-is-clickable behavior was silently
                        // unchecking FOC (and wiping the reason) on a stray
                        // click anywhere near the text. The checkbox below
                        // stops its own click from bubbling here, so it's
                        // the only thing that can check/uncheck.
                        onClick={() => {
                          if (!row.isFoc || id) return;
                          setExpandedFocReasons((prev) => ({
                            ...prev,
                            [idx]: !prev[idx],
                          }));
                        }}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: row.isFoc && !id ? "pointer" : "default",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={!!row.isFoc}
                          disabled={!!id}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const updated = [...formik.values.items];
                            updated[idx] = {
                              ...updated[idx],
                              isFoc: checked,
                              focReason: checked ? updated[idx].focReason || "" : "",
                              // Discount is kept as-is either way — FOC just
                              // means this line isn't billed, but the price/
                              // discount fields stay live so the record
                              // still shows what it would have cost.
                            };
                            if (!checked) {
                              setExpandedFocReasons((prev) => {
                                if (!prev[idx]) return prev;
                                const next = { ...prev };
                                delete next[idx];
                                return next;
                              });
                            }
                            recalcSummary(updated);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: "#0c54a0" }}
                        >
                          FOC / Complimentary
                          <Box
                            component="span"
                            sx={{ color: "text.secondary", fontWeight: 400 }}
                          >
                            {" "}
                            — no charge
                          </Box>
                        </Typography>
                        {row.isFoc && !id && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              color: "text.secondary",
                              ml: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontSize: 12, textDecoration: "underline" }}
                            >
                              {row.focReason ? "Edit reason" : "Add reason"}
                            </Typography>
                            {expandedFocReasons[idx] ? (
                              <ExpandLessIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        )}
                        {row.isFoc && !expandedFocReasons[idx] && row.focReason && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontStyle: "italic",
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            “{row.focReason}”
                          </Typography>
                        )}
                      </Box>
                      {row.isFoc && (
                        <Collapse in={isViewOnly || !!id || !!expandedFocReasons[idx]}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={isViewOnly ? 3 : 2}
                            maxRows={isViewOnly ? 6 : 4}
                            placeholder="Reason (optional) — e.g. damaged replacement, promo gift"
                            disabled={!!id && !isViewOnly}
                            InputProps={{
                              readOnly: isViewOnly,
                              sx: {
                                alignItems: "flex-start",
                                "& textarea": {
                                  maxHeight: isViewOnly ? 160 : 120,
                                  overflowY: "auto !important",
                                  overscrollBehavior: "contain",
                                },
                              },
                            }}
                            value={row.focReason || ""}
                            onChange={(e) => {
                              const updated = [...formik.values.items];
                              updated[idx] = {
                                ...updated[idx],
                                focReason: e.target.value,
                              };
                              formik.setFieldValue("items", updated);
                            }}
                            sx={{
                              mt: 0.75,
                              maxWidth: isViewOnly ? 720 : 480,
                              ...(isViewOnly && { pointerEvents: "auto" }),
                            }}
                          />
                        </Collapse>
                      )}
                </Box>
              ) : null;

              if (!isFocCard) return rowFieldsGrid;

              return (
                <Box
                  sx={{
                    border: "1px solid #E3E6EA",
                    borderRadius: 2,
                    overflow: "hidden",
                    mt: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>{rowFieldsGrid}</Box>
                  {focFooter}
                </Box>
              );
              })()}

                {isLastBundleRow &&
                  (() => {
                    const grp = formik.values.items.filter(
                      (it) => it.bundleGroupId === row.bundleGroupId
                    );
                    const grpMode = row.discountMode || "bundle";
                    const groupGrossTotal = grp.reduce(
                      (s, it) =>
                        s +
                        (parseFloat(it.sellingPrice || 0) || 0) *
                          (parseFloat(it.quantity || 0) || 0),
                      0
                    );
                    const groupAfterItemDisc =
                      grpMode === "item"
                        ? grp.reduce((s, it) => {
                            const gross =
                              parseFloat(it.sellingPrice || 0) *
                              (parseFloat(it.quantity || 0) || 0);
                            const itDisc = discountAmountFromPercent(
                              gross,
                              it.discountPercent
                            );
                            return s + (gross - itDisc);
                          }, 0)
                        : Math.max(
                            groupGrossTotal -
                              discountAmountFromPercent(groupGrossTotal, row.bundleDiscount),
                            0
                          );
                    return (
                      <Grid item xs={12} sx={{ mt: 0 }}>
                        <Box
                          sx={{
                            backgroundColor: "#EEF4FF",
                            border: "1px solid #B8D7FF",
                            borderTop: "none",
                            px: 3,
                            py: 1.25,
                            borderRadius: "0 0 8px 8px",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: "#0c54a0" }}>
                            Bundle total {formatAED(groupAfterItemDisc)}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })()}
              </Fragment>
            );
            })}

            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addRow}
                variant="outlined"
              >
                {selectionMode === "bundle" ? "Add Bundle" : "Add Product"}
              </Button>
            </Box>

            {/* Removed global bundle discount: per-bundle discounts are entered on each bundle header */}

            </Box>
            </Box>

            <Box sx={{ minWidth: 0 }}>
            {/* Summary */}
            <Box
              sx={{
                border: "1px solid #E3E6EA",
                borderRadius: "14px",
                p: { xs: 2, sm: 3.5 },
                mb: 3,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: "#EEF2FF",
                    color: "#2F5FED",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  3
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Summary
                </Typography>
              </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}></Grid>
              <Grid item xs={12} md={7}>
                {/* One consistent 3-column grid per row: label | control |
                    amount(right). The running total reads top-to-bottom:
                    Subtotal - Discount + Tax + Shipping = Grand Total. Subtotal
                    is the GROSS line total (pre-discount) so it stays fixed when
                    the discount changes; the discount is its own line right
                    below it, and Tax reads as a % of the post-discount amount. */}

                {/* Subtotal (gross, pre-discount) */}
                <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item xs={9}>
                    <Typography color="text.secondary">Subtotal</Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography>
                      {(() => {
                        // FOC lines contribute 0 to r.subtotal by design
                        // (they're never billed), but this Subtotal display
                        // should still read as "what these products are
                        // worth" — use r.focValue (the would-be price) for
                        // any FOC row instead, purely informational. Grand
                        // Total itself is computed elsewhere and stays
                        // unaffected — it never counts FOC lines either way.
                        // Always the plain net figure — tax math only shows
                        // up in the Tax / Grand Total rows below, never here.
                        const grossSubtotal = formik.values.items.reduce(
                          (s, r) =>
                            s +
                            (parseFloat(r.isFoc ? r.focValue : r.subtotal) || 0),
                          0
                        );
                        return formatAED(grossSubtotal);
                      })()}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Less FOC — the Subtotal above includes free items at their
                    would-be price, so take them back out here; otherwise
                    Subtotal + Tax never adds up to the Grand Total. */}
                {(() => {
                  const focTotal = formik.values.items.reduce(
                    (s, r) => s + (r.isFoc ? parseFloat(r.focValue) || 0 : 0),
                    0
                  );
                  if (focTotal <= 0) return null;
                  return (
                    <Grid container spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                      <Grid item xs={9}>
                        <Typography color="text.secondary">Less: FOC (free items)</Typography>
                      </Grid>
                      <Grid item xs={3} textAlign="right">
                        <Typography sx={{ color: "#0c54a0" }}>- {formatAED(focTotal)}</Typography>
                      </Grid>
                    </Grid>
                  );
                })()}

                {/* Discount Method now lives at the top of the Product
                    Selection card (set before entering line items) — see
                    the block right after the Products/Bundle toggle. */}

                {/* Special Discount */}
                <Grid container spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <Grid item xs={3.5}>
                    <Typography color="text.secondary">
                      Special Discount
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value (AED)"
                      name="discount"
                      disabled={
                        selectionMode === "product" &&
                        formik.values.discountType !== "total"
                      }
                      value={formik.values.discount}
                      onChange={(e) => {
                        const subtotal = formik.values.items.reduce(
                          (sum, row) => sum + (parseFloat(row.subtotal) || 0),
                          0
                        );
                        const maxAllowed = Math.min(MAX_MONEY_VALUE, subtotal);
                        const value = sanitizeMoneyInput(
                          e.target.value,
                          maxAllowed
                        );
                        const numVal = parseFloat(value) || 0;
                        const pct = subtotal > 0 ? ((numVal / subtotal) * 100).toFixed(2) : "";

                        recalcSummary(
                          formik.values.items,
                          formik.values.shippingCharges,
                          value
                        );
                        formik.setFieldValue("discount", value);
                        formik.setFieldValue("discountPercent", pct);
                      }}
                      onFocus={(e) => e.target.select()}
                      inputProps={{
                        inputMode: "decimal",
                      }}
                      onBlur={(e) => {
                        formik.setFieldTouched("discount", true);
                        let val = parseFloat(e.target.value || 0);
                        if (!isNaN(val)) {
                          val = val.toFixed(2);
                          formik.setFieldValue("discount", val);
                        } else {
                          formik.setFieldValue("discount", "");
                        }
                      }}
                      error={
                        formik.touched.discount &&
                        Boolean(formik.errors.discount)
                      }
                      helperText={
                        formik.touched.discount && formik.errors.discount
                      }
                    />
                  </Grid>
                  <Grid item xs={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="%"
                      name="discountPercent"
                      disabled={
                        selectionMode === "product" &&
                        formik.values.discountType !== "total"
                      }
                      value={formik.values.discountPercent || ""}
                      onChange={(e) => {
                        const pct = sanitizePercentInput(e.target.value);
                        const subtotal = formik.values.items.reduce(
                          (sum, row) => sum + (parseFloat(row.subtotal) || 0),
                          0
                        );
                        const discVal = subtotal > 0 ? discountAmountFromPercent(subtotal, pct) : 0;
                        const valStr = discVal > 0 ? discVal.toFixed(2) : "0.00";

                        recalcSummary(
                          formik.values.items,
                          formik.values.shippingCharges,
                          valStr
                        );
                        formik.setFieldValue("discountPercent", pct);
                        formik.setFieldValue("discount", valStr);
                      }}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography>
                      {Number(formik.values.discount) > 0 ? "- " : ""}
                      {formatAED(formik.values.discount || 0)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Tax Mode and Tax Rate now live at the top of the Product
                    Selection card (set before entering line items) — see
                    the block right after the Products/Bundle toggle. */}

                {/* Amount before VAT (only meaningful in inclusive mode,
                    where Tax below is extracted out of Grand Total rather
                    than added on top) */}
                {!allItemsFoc && formik.values.taxMode === "inclusive" && (
                  <>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={9}>
                      <Typography color="text.secondary">
                        Amount before VAT
                      </Typography>
                    </Grid>
                    <Grid item xs={3} textAlign="right">
                      <Typography>
                        {formatAED(formik.values.subtotalSummary || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                  </>
                )}

                {/* Tax — hidden once every product is FOC (no VAT on a
                    fully-complimentary invoice), unless the document does
                    carry VAT (FOC requests saved before the no-VAT rule), so
                    the Grand Total is never an unexplained number. */}
                {(!allItemsFoc || Number(formik.values.taxAmount) > 0) && (
                  <Grid container spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                    <Grid item xs={9}>
                      <Typography color="text.secondary">
                        Tax ({parseFloat(formik.values.taxType) || 0}%)
                      </Typography>
                    </Grid>
                    <Grid item xs={3} textAlign="right">
                      <Typography>
                        {Number(formik.values.taxAmount) > 0 ? "+ " : ""}
                        {formatAED(formik.values.taxAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {/* Shipping Charges */}
                <Grid container spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <Grid item xs={6}>
                    <Typography color="text.secondary">
                      Shipping Charges
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      name="shippingCharges"
                      type="number"
                      value={formik.values.shippingCharges}
                      inputProps={{
                        inputMode: "decimal",
                      }}
                      onChange={(e) => {
                        const value = sanitizeMoneyInput(
                          e.target.value,
                          MAX_MONEY_VALUE
                        );

                        recalcSummary(
                          formik.values.items,
                          value, // shippingOverride
                          formik.values.discount, // ✅ discountOverride
                          formik.values.taxType // ✅ taxTypeOverride
                        );
                        formik.setFieldValue("shippingCharges", value);
                      }}
                      onFocus={(e) => e.target.select()}
                      onBlur={(e) => {
                        formik.setFieldTouched("shippingCharges", true);
                        let val = parseFloat(e.target.value || 0);
                        if (!isNaN(val)) {
                          val = val.toFixed(2);
                          formik.setFieldValue("shippingCharges", val);
                        } else {
                          formik.setFieldValue("shippingCharges", "");
                        }
                      }}
                      error={
                        formik.touched.shippingCharges &&
                        Boolean(formik.errors.shippingCharges)
                      }
                      helperText={
                        formik.touched.shippingCharges &&
                        formik.errors.shippingCharges
                      }
                    />
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography>
                      {Number(formik.values.shippingCharges) > 0 ? "+ " : ""}
                      {formatAED(formik.values.shippingCharges || 0)}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {/* Grand Total is always a plain, read-only result — never
                    directly editable, in either tax mode. It's driven
                    entirely by the product rows' own prices above: what
                    changes between Exclusive and Inclusive is how those
                    unit prices are READ (see recalcSummary), not some
                    separate typed target that gets back-solved into a
                    discount. */}
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={9}>
                    <Typography fontWeight={700}>Grand Total</Typography>
                  </Grid>
                  <Grid item xs={3} textAlign="right">
                    <Typography fontWeight={700}>
                      {formatAED(formik.values.grandTotal)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            </Box>

            {/* Payment & Notes */}
            <Box
              sx={{
                border: "1px solid #E3E6EA",
                borderRadius: "14px",
                p: { xs: 2, sm: 3.5 },
                mb: 3,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: "#EEF2FF",
                    color: "#2F5FED",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  4
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Payment & Notes
                </Typography>
              </Box>

            {/* Payment Method */}
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6} lg={4}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.payment_type &&
                    Boolean(formik.errors.payment_type)
                  }
                >
                  <InputLabel id="payment-type-label">Payment Type</InputLabel>

                  <Select
                    labelId="payment-type-label"
                    id="payment_type"
                    name="payment_type"
                    value={formik.values.payment_type || ""}
                    label="Payment Type"
                    onChange={(e) => {
                      if (e.target.value === 1 && allItemsFoc) {
                        // Blocked: every item on this order is FOC — nothing
                        // left to actually bill on Credit (see the matching
                        // useEffect above, which forces this back to
                        // Immediate Payment if the last non-FOC item gets
                        // marked FOC after Credit was already selected). A
                        // mixed order (some FOC, some paid) is unaffected.
                        return;
                      }
                      formik.handleChange(e);
                      if (e.target.value === 1) {
                        formik.setFieldValue("paymentMethod", "");
                        // Give it a sensible starting point the user can
                        // change below — not left blank.
                        if (!formik.values.creditDays) {
                          formik.setFieldValue("creditDays", 15);
                          formik.setFieldValue(
                            "dueDate",
                            dayjs(formik.values.orderDate || undefined)
                              .add(15, "day")
                              .format("YYYY-MM-DD")
                          );
                        }
                      }
                    }}
                    onBlur={formik.handleBlur}
                  >
                    {(() => {
                      // Credit is approved per invoice: always selectable on a
                      // new invoice (it goes to Admin on submit); on edit only
                      // if this invoice was already approved for credit.
                      const creditBlockedByFoc = allItemsFoc;
                      const creditBlockedByEdit = Boolean(id) && !wasCredit;
                      const creditDisabled = creditBlockedByFoc || creditBlockedByEdit;
                      const creditMenuItem = (
                        <MenuItem value={1} disabled={creditDisabled}>
                          Credit Payment Terms
                        </MenuItem>
                      );
                      if (!creditDisabled) return creditMenuItem;
                      return (
                        <Tooltip
                          title={
                            creditBlockedByFoc
                              ? "Every item on this order is FOC — there's nothing to bill on Credit terms. Select Immediate Payment instead."
                              : "Credit is approved per invoice. This invoice wasn't approved for credit — create a new invoice to request it."
                          }
                          placement="right"
                        >
                          <span>{creditMenuItem}</span>
                        </Tooltip>
                      );
                    })()}
                    <MenuItem value={2}>Immediate Payment</MenuItem>
                  </Select>

                  {formik.touched.payment_type &&
                    formik.errors.payment_type && (
                      <FormHelperText>
                        {formik.errors.payment_type}
                      </FormHelperText>
                    )}
                  {!id && formik.values.payment_type == 1 && !allItemsFoc && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      icon={<InfoOutlinedIcon fontSize="small" />}
                      sx={{ mt: 1.25, borderRadius: 1.5, py: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                        Credit needs approval from Admin.
                      </Typography>
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              {formik.values.payment_type == 2 && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Method"
                    name="paymentMethod"
                    value={formik.values.paymentMethod}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.paymentMethod &&
                      Boolean(formik.errors.paymentMethod)
                    }
                    helperText={
                      formik.touched.paymentMethod &&
                      formik.errors.paymentMethod
                    }
                  >
                    {paymentOptions.map((opt) => {
                      const disabled = opt.requiresFoc && !hasFocItem;
                      const menuItem = (
                        <MenuItem key={opt.value} value={opt.value} disabled={disabled}>
                          {opt.label}
                        </MenuItem>
                      );
                      if (!disabled) return menuItem;
                      // A disabled MenuItem swallows hover events, so the
                      // Tooltip needs the extra <span> wrapper to still fire.
                      return (
                        <Tooltip
                          key={opt.value}
                          title="Mark at least one item on this order as FOC to select Complimentary."
                          placement="right"
                        >
                          <span>{menuItem}</span>
                        </Tooltip>
                      );
                    })}
                  </TextField>
                </Grid>
              )}

              {/* Reference No. & payment proof — shown once Immediate Payment
                  and a payment method are both selected (Item 4). Not shown
                  for Complimentary — there's no actual payment to reference
                  or attach proof of. */}
              {formik.values.payment_type == 2 &&
                formik.values.paymentMethod &&
                formik.values.paymentMethod !== "8" && (
                <>
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label="Transaction Id"
                      name="paymentReferenceNo"
                      value={formik.values.paymentReferenceNo || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  {/* pointerEvents re-enabled here specifically — the "View
                      attachment" link is a legitimate read action even in
                      view-only mode; readOnly (below) already drops the
                      Remove button so nothing editable is exposed. */}
                  <Grid item xs={12} md={6} lg={4} sx={isViewOnly ? { pointerEvents: "auto" } : undefined}>
                    <PaymentAttachmentUpload
                      url={formik.values.paymentAttachmentUrl}
                      name={formik.values.paymentAttachmentName}
                      uploading={formik.values.paymentAttachmentUploading}
                      readOnly={isViewOnly}
                      uploadApi={uploadToVercelAPI}
                      onUploadStart={() =>
                        formik.setFieldValue("paymentAttachmentUploading", true)
                      }
                      onUploadSuccess={({ url, name }) => {
                        formik.setFieldValue("paymentAttachmentUrl", url);
                        formik.setFieldValue("paymentAttachmentName", name);
                        formik.setFieldValue("paymentAttachmentUploading", false);
                      }}
                      onUploadError={(message) => {
                        formik.setFieldValue("paymentAttachmentUploading", false);
                        alertRef.current.triggerAlert(
                          JSON.stringify({ message, messageType: "error" })
                        );
                      }}
                      onRemove={() => {
                        formik.setFieldValue("paymentAttachmentUrl", "");
                        formik.setFieldValue("paymentAttachmentName", "");
                      }}
                    />
                  </Grid>
                </>
              )}

              {formik.values.payment_type == 1 && (
                <>
                  {/* Creating: the days the user is requesting — editable,
                      goes to Admin with the rest of the invoice. Editing an
                      already-approved invoice: locked to what Admin approved. */}
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      label={id ? "Credit Days (approved by Admin)" : "Credit Days Requested"}
                      type="number"
                      value={formik.values.creditDays || ""}
                      onChange={(e) => {
                        if (id) return;
                        let days = parseInt(e.target.value, 10);
                        if (Number.isNaN(days)) days = "";
                        // Cap at 10 years — an unbounded value here overflows
                        // dayjs().add() into "Invalid Date" on the Due Date
                        // field below.
                        if (days && days > MAX_CREDIT_DAYS) days = MAX_CREDIT_DAYS;
                        formik.setFieldValue("creditDays", days);
                        formik.setFieldValue(
                          "dueDate",
                          days
                            ? dayjs(formik.values.orderDate || undefined)
                                .add(Number(days), "day")
                                .format("YYYY-MM-DD")
                            : ""
                        );
                      }}
                      fullWidth
                      disabled={Boolean(id)}
                      inputProps={{ min: 1, max: MAX_CREDIT_DAYS }}
                      helperText={
                        id
                          ? "Approved by Admin for this invoice"
                          : "How many days you're asking for — Admin can adjust this"
                      }
                    />
                  </Grid>

                  {/* Due Date (Auto Calculated) */}
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      label="Due Date"
                      value={
                        formik.values.dueDate
                          ? dayjs(formik.values.dueDate).format("DD-MM-YYYY")
                          : ""
                      }
                      fullWidth
                      disabled
                    />
                  </Grid>

                  {/* Why credit is being requested — shown to Admin next to
                    the invoice in the approval list, separate from the
                    invoice's own Notes field below. */}
                  {!id && (
                    <Grid item xs={12}>
                      <TextField
                        label="Reason for Credit Request (optional)"
                        placeholder="e.g. regular customer, first order on credit, etc."
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Payment Method Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Invoice Notes"
                  name="notes"
                  value={formik.values.notes}
                  onChange={(e) => {
                    let value = e.target.value;
                    value = value.charAt(0).toUpperCase() + value.slice(1);
                    formik.setFieldValue("notes", value);
                  }}
                />
              </Grid>
            </Grid>

            </Box>

            {/* Terms and Conditions */}
            <Box
              sx={{
                border: "1px solid #E3E6EA",
                borderRadius: "14px",
                p: { xs: 2, sm: 3.5 },
                mb: 3,
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: "#EEF2FF",
                    color: "#2F5FED",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  5
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Terms & Conditions
                </Typography>
              </Box>
            <Grid
              item
              xs={12}
              md={4}
              lg={4}
              textAlign="left"
              className="po_lebleterm"
            >
              <FormControl
                className="form_control"
                sx={{ width: "100%" }}
                error={formik.touched.term_id && Boolean(formik.errors.term_id)} // put error on FormControl
              >
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  value={formik.values.term_id}
                  onChange={(event) => {
                    formik.setFieldValue("term_id", event.target.value);
                    const terms = termsAndConditions.find(
                      (term) => term.id === event.target.value
                    );
                    formik.setFieldValue("terms", terms?.description || "");
                  }}
                  input={<OutlinedInput />}
                >
                  {termsAndConditions?.map((terms) => (
                    <MenuItem key={terms?.id} value={terms?.id}>
                      <ListItemText primary={terms?.title} />
                    </MenuItem>
                  ))}
                </Select>

                {/* This is where the error text shows */}
                {formik.touched.term_id && formik.errors.term_id && (
                  <FormHelperText>{formik.errors.term_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {formik.values.terms !== "" && (
              <Grid item lg={12} md={12} sm={12} mt={2}>
                <ReactQuill
                  theme="snow"
                  value={formik.values.terms}
                  onChange={(content) => formik.setFieldValue("terms", content)} // Store as HTML
                  style={{ height: "200px", marginBottom: "40px" }}
                />
                {formik.touched.terms && formik.errors.terms && (
                  <p style={{ color: "red", fontSize: "12px" }}>
                    {formik.errors.terms}
                  </p>
                )}
              </Grid>
            )}
            </Box>

            {/* Submit — omitted entirely in view-only mode (it would sit
                inert under pointerEvents:none anyway; a real, clickable Back
                button lives just below, outside the locked wrapper). */}
            {!isViewOnly && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  onClick={handleSubmitWithValidation}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ px: 6, py: 1.3, borderRadius: "10px", fontWeight: 700, fontSize: 15, textTransform: "none", boxShadow: "0 2px 6px rgba(12,84,160,0.25)" }}
                >
                  {isEdit ? "Update Order" : "Create Order"}
                </Button>
              </Box>
            )}
            </Box>
          </Box>
        </form>
        {isViewOnly && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="outlined"
              onClick={() => navigate("/foc/approvals")}
              startIcon={<ArrowBackIosIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Back to FOC Approvals
            </Button>
          </Box>
        )}
      </Grid>
      {/* </Box> */}
    </Layout>
  );
};

export default Orders;
